// Rik.ai — Customer Portal
// customer.rikai.tech — companies that commission surveys
import React, { useState, useEffect, useCallback, useRef } from "react";
import { downloadReportPdf } from "./ReportPdf";
import AskYourDataPanel from "./AskYourData/AskYourDataPanel";

// ══════════════════════════════════════════════════════════════════════════════
// RESPONSIVE HOOK
// ══════════════════════════════════════════════════════════════════════════════
function useViewport() {
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return { width, isMobile: width < 640, isTablet: width >= 640 && width < 1024 };
}

// ══════════════════════════════════════════════════════════════════════════════
// DESIGN TOKENS — professional B2B palette (slate + indigo + teal)
// Rik.ai brand purple is preserved only in the wordmark gradient.
// ══════════════════════════════════════════════════════════════════════════════
const C = {
  bg:         "#0A0F1C",         // deep slate navy
  surface:    "#111826",         // slate-900
  card:       "#161E2E",         // elevated slate
  cardHover:  "#1C2538",
  border:     "#273449",         // cool slate border
  accent:     "#4F6EF7",         // indigo-blue — primary action
  accentDim:  "rgba(79,110,247,0.12)",
  accentHover:"#3B57E8",
  blue:       "#3B82F6",
  cyan:       "#14B8A6",         // teal — secondary accent (replaces bright cyan)
  cyanBright: "#22D3EE",         // kept for wordmark only
  gold:       "#F59E0B",
  text:       "#F1F5F9",         // slate-100
  textDim:    "#CBD5E1",         // slate-300
  muted:      "#8894A8",         // slate-400
  danger:     "#EF4444",
  dangerDim:  "rgba(239,68,68,0.12)",
  warning:    "#F59E0B",
  success:    "#10B981",         // emerald
  // Legacy alias — points to accent so existing `C.purple` usages become indigo
  purple:     "#4F6EF7",
  brandPurple:"#A78BFA",         // used ONLY in Rik.ai wordmark gradient
};

// ══════════════════════════════════════════════════════════════════════════════
// API HELPERS — proxied to http://localhost:3001 via package.json "proxy"
// ══════════════════════════════════════════════════════════════════════════════
async function api(method, path, body, { skipAuthRedirect = false } = {}) {
  const opts = { method, credentials: "include", headers: { "Content-Type": "application/json" } };
  if (body) opts.body = JSON.stringify(body);
  const res  = await fetch(`/api${path}`, opts);
  const data = await res.json().catch(() => ({}));
  if (res.status === 401 && !skipAuthRedirect) {
    // Token expired or cleared — redirect to login.
    // Not used for the login call itself: there's no session to "expire" on a
    // fresh login attempt, and the backend legitimately returns 401 for wrong
    // credentials — skipAuthRedirect lets that fall through to the generic
    // !res.ok branch below so the real error message reaches the user.
    window.__cpLogout?.();
    throw new Error("Session expired. Please log in again.");
  }
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}
const surveyApi = api; // same underlying call

// ══════════════════════════════════════════════════════════════════════════════
// SHARED UI PRIMITIVES
// ══════════════════════════════════════════════════════════════════════════════
function Btn({ children, onClick, variant = "primary", style, disabled, type = "button" }) {
  const base = {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "9px 18px", borderRadius: 8, fontSize: 14, fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer", border: "none",
    transition: "all 0.15s", opacity: disabled ? 0.5 : 1, fontFamily: "inherit",
    ...style,
  };
  const variants = {
    primary: { background: C.accent, color: "#fff" },
    outline: { background: "transparent", color: C.accent, border: `1.5px solid ${C.accent}` },
    ghost:   { background: "transparent", color: C.muted,  border: `1.5px solid ${C.border}` },
    danger:  { background: C.danger, color: "#fff" },
  };
  return (
    <button type={type} style={{ ...base, ...variants[variant] }} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

function Card({ children, style, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      style={{
        background: C.card, borderRadius: 12, border: `1px solid ${C.border}`,
        padding: 20, transition: "all 0.2s",
        ...(onClick ? { cursor: "pointer", transform: hov ? "translateY(-2px)" : "none", borderColor: hov ? C.purple : C.border } : {}),
        ...style,
      }}
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >{children}</div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, required, disabled, style }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, ...style }}>
      {label && <label style={{ fontSize: 13, color: C.muted, fontWeight: 500 }}>{label}{required && <span style={{ color: C.danger }}> *</span>}</label>}
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} disabled={disabled}
        onAnimationStart={(e) => { if (e.animationName === "rikAutofillStart") onChange(e.target.value); }}
        style={{
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
          color: C.text, fontSize: 14, padding: "10px 12px", outline: "none", fontFamily: "inherit",
        }}
      />
    </div>
  );
}

function Textarea({ label, value, onChange, placeholder, rows = 3, style }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, ...style }}>
      {label && <label style={{ fontSize: 13, color: C.muted, fontWeight: 500 }}>{label}</label>}
      <textarea
        value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} rows={rows}
        style={{
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
          color: C.text, fontSize: 14, padding: "10px 12px", outline: "none",
          resize: "vertical", fontFamily: "inherit",
        }}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, style }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, ...style }}>
      {label && <label style={{ fontSize: 13, color: C.muted, fontWeight: 500 }}>{label}</label>}
      <select
        value={value} onChange={(e) => onChange(e.target.value)}
        style={{
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
          color: C.text, fontSize: 14, padding: "10px 12px", outline: "none", fontFamily: "inherit",
        }}
      >
        {options.map((o) => (
          <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
        ))}
      </select>
    </div>
  );
}

function MultiChip({ label, options, value = [], onChange, style }) {
  const toggle = (opt) => {
    if (value.includes(opt)) onChange(value.filter((v) => v !== opt));
    else onChange([...value, opt]);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, ...style }}>
      {label && <label style={{ fontSize: 13, color: C.muted, fontWeight: 500 }}>{label}</label>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {options.map((opt) => {
          const sel = value.includes(opt);
          return (
            <button
              key={opt} type="button" onClick={() => toggle(opt)}
              style={{
                padding: "5px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer",
                border: `1.5px solid ${sel ? C.purple : C.border}`,
                background: sel ? C.accentDim : "transparent",
                color: sel ? C.purple : C.muted, fontFamily: "inherit",
              }}
            >{opt}</button>
          );
        })}
      </div>
    </div>
  );
}

function Toast({ message, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div style={{
      position: "fixed", bottom: 24, right: 24, zIndex: 9999,
      background: C.accent, color: "#fff",
      padding: "12px 20px", borderRadius: 10, fontSize: 14, fontWeight: 600,
      boxShadow: "0 8px 32px rgba(79,110,247,0.35)", animation: "fadeIn 0.3s",
    }}>{message}</div>
  );
}

function StatusBadge({ status }) {
  const s = (status || "").toLowerCase();
  const map = {
    draft:          { bg: "#1f2937", color: "#9ca3af", label: "DRAFT" },
    active:         { bg: C.accentDim, color: C.purple, label: "ACTIVE" },
    closed:         { bg: "rgba(20,184,166,0.15)", color: C.cyan, label: "CLOSED" },
    pending:        { bg: "rgba(245,158,11,0.15)", color: C.warning, label: "PENDING" },
    completed:      { bg: "rgba(20,184,166,0.15)", color: C.cyan, label: "COMPLETED" },
    declined:       { bg: C.dangerDim, color: C.danger, label: "DECLINED" },
    in_progress:    { bg: "rgba(245,158,11,0.15)", color: C.warning, label: "IN PROGRESS" },
    expired:        { bg: "#1f2937", color: "#6b7280", label: "EXPIRED" },
    pending_review: { bg: "rgba(245,158,11,0.15)", color: "#F59E0B", label: "AWAITING APPROVAL" },
    approved:       { bg: "rgba(34,197,94,0.15)", color: "#22C55E", label: "APPROVED" },
  };
  const st = map[s] || { bg: "#1f2937", color: "#9ca3af", label: status || "—" };
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 20,
      fontSize: 10, fontWeight: 700, letterSpacing: "0.5px",
      background: st.bg, color: st.color, textTransform: "uppercase",
    }}>{st.label}</span>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// HEADER
// ══════════════════════════════════════════════════════════════════════════════
function Header({ customer, onSignOut, onDashboard, onSupport, onProfile }) {
  const { isMobile } = useViewport();
  return (
    <header style={{
      background: C.card, borderBottom: `1px solid ${C.border}`,
      padding: isMobile ? "12px 16px" : "14px 32px",
      display: "flex", alignItems: "center",
      justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100,
    }}>
      <div onClick={onDashboard} style={{ cursor: "pointer" }}>
        {/* Single inline SVG: full logo PNG (yellow dot visible) + text sized to span orb-body height only.
            Yellow dot occupies top ~15% of PNG (y=0..5 in rendered 32px); orb body spans y=6..32 (26px).
            viewBox widened + overflow:visible so CUSTOMER PORTAL can never be clipped. */}
        <svg viewBox="0 0 160 32" height={isMobile ? 28 : 32} overflow="visible" style={{ display: "block", overflow: "visible" }}>
          <defs>
            <linearGradient id="hdrAiGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#A78BFA"/>
              <stop offset="100%" stopColor="#22D3EE"/>
            </linearGradient>
          </defs>
          {/* Original logo PNG — full image, yellow dot included, no clipping */}
          <image href="/rik-logo.png" x="0" y="0" width="32" height="32" preserveAspectRatio="xMidYMid meet"/>
          {/* "Rik.ai" — fontSize 16, baseline y=17 → cap-top ~5.5 = top of ऋ orb body (just below yellow dot) */}
          <text x="36" y="17" fontFamily="Poppins,system-ui,-apple-system,sans-serif" fontSize="16" fontWeight="800">
            <tspan fill="#F1F5F9">Rik</tspan><tspan fill="url(#hdrAiGrad)">.ai</tspan>
          </text>
          {/* "CUSTOMER PORTAL" — fontSize 9, baseline y=31 → bottom aligns with bottom of orb body (y=32) */}
          <text x="36" y="31" fontFamily="Poppins,system-ui,-apple-system,sans-serif" fontSize="9" letterSpacing="0.6" fill="#6B7280">CUSTOMER PORTAL</text>
        </svg>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 16 }}>
        {customer && !isMobile && (
          <div onClick={onProfile} style={{ textAlign: "right", cursor: "pointer" }} title="My Profile">
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
              {customer.firstName} {customer.lastName}
            </div>
            <div style={{ fontSize: 11, color: C.muted }}>
              {customer.company?.companyName} · {customer.role}
            </div>
          </div>
        )}
        {customer && isMobile && (
          <div onClick={onProfile} style={{ fontSize: 12, fontWeight: 600, color: C.text, cursor: "pointer" }}>
            {customer.firstName || customer.company?.companyName}
          </div>
        )}
        <button onClick={onSupport} style={{
          background: "transparent", border: `1px solid ${C.border}`,
          color: C.muted, padding: isMobile ? "6px 10px" : "7px 14px", borderRadius: 8,
          fontSize: 12, cursor: "pointer", fontFamily: "inherit", minHeight: 36,
        }}>Support</button>
        <button onClick={onSignOut} style={{
          background: "transparent", border: `1px solid ${C.border}`,
          color: C.muted, padding: isMobile ? "6px 10px" : "7px 14px", borderRadius: 8,
          fontSize: 12, cursor: "pointer", fontFamily: "inherit", minHeight: 36,
        }}>Sign out</button>
      </div>
    </header>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// CONSENT GATE SCREEN
// Shown when a new PP or ToS version has been published since the user last accepted.
// ══════════════════════════════════════════════════════════════════════════════
function ConsentGateScreen({ documents, onAccepted }) {
  const [ppChecked,  setPpChecked]  = useState(false);
  const [tosChecked, setTosChecked] = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");

  const needsPP  = documents.includes("privacy_policy");
  const needsToS = documents.includes("terms_of_service");
  const ppOk     = !needsPP  || ppChecked;
  const tosOk    = !needsToS || tosChecked;
  const canProceed = ppOk && tosOk;

  const handleAccept = async () => {
    setLoading(true); setError("");
    try {
      await api("POST", "/customer-portal/legal/consent", { documents });
      onAccepted();
    } catch (err) {
      setError(err.message || "Failed to record consent. Please try again.");
      setLoading(false);
    }
  };

  const card = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: "32px 28px", maxWidth: 520, margin: "0 auto", width: "100%" };
  const lbl  = { display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer", padding: "14px 16px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px", fontFamily: "'Poppins','Segoe UI',Arial,sans-serif" }}>
      <div style={card}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 52, height: 52, borderRadius: "50%", background: `linear-gradient(135deg,${C.purple}30,${C.blue}20)`, border: `1px solid ${C.purple}40`, marginBottom: 16 }}>
            <span style={{ fontSize: 24 }}>📋</span>
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text, margin: 0, marginBottom: 8 }}>Our terms have been updated</h2>
          <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, margin: 0 }}>
            Please review and accept the updated{" "}
            {needsPP && needsToS ? "Privacy Policy and Terms of Service" : needsPP ? "Privacy Policy" : "Terms of Service"}
            {" "}to continue using the Rik.ai Customer Portal.
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
          {needsToS && (
            <label style={lbl}>
              <input type="checkbox" checked={tosChecked} onChange={e => setTosChecked(e.target.checked)}
                style={{ marginTop: 2, flexShrink: 0, accentColor: C.purple, width: 16, height: 16, cursor: "pointer" }} />
              <span style={{ color: C.muted, fontSize: 13, lineHeight: 1.55 }}>
                I have read and agree to the updated{" "}
                <a href="/api/legal/customer/terms_of_service/page" target="_blank" rel="noopener" style={{ color: C.purple }} onClick={e => e.stopPropagation()}>Terms of Service</a>.
              </span>
            </label>
          )}
          {needsPP && (
            <label style={lbl}>
              <input type="checkbox" checked={ppChecked} onChange={e => setPpChecked(e.target.checked)}
                style={{ marginTop: 2, flexShrink: 0, accentColor: C.purple, width: 16, height: 16, cursor: "pointer" }} />
              <span style={{ color: C.muted, fontSize: 13, lineHeight: 1.55 }}>
                I have read and agree to the updated{" "}
                <a href="/api/legal/customer/privacy_policy/page" target="_blank" rel="noopener" style={{ color: C.purple }} onClick={e => e.stopPropagation()}>Privacy Policy</a>.
              </span>
            </label>
          )}
        </div>

        {error && <p style={{ color: "#F87171", fontSize: 12, marginBottom: 16, padding: "10px 14px", background: "rgba(248,113,113,0.08)", borderRadius: 8 }}>{error}</p>}

        <Btn onClick={handleAccept} disabled={!canProceed || loading} style={{ width: "100%" }}>
          {loading ? "Saving…" : "Accept and Continue →"}
        </Btn>

        <p style={{ color: C.muted, fontSize: 11, textAlign: "center", marginTop: 12, lineHeight: 1.5 }}>
          You must accept the updated terms to continue. Questions?{" "}
          <a href="mailto:contact@rikai.tech" style={{ color: C.muted, textDecoration: "underline" }}>contact@rikai.tech</a>
        </p>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// LOGIN PAGE
// ══════════════════════════════════════════════════════════════════════════════
function LoginPage({ onLogin }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);

  // Inject CSS autofill keyframe so Chrome fires onAnimationStart on autofilled inputs
  useEffect(() => {
    if (document.getElementById("rik-autofill-style")) return;
    const s = document.createElement("style");
    s.id = "rik-autofill-style";
    s.textContent = "@keyframes rikAutofillStart{from{}to{}}input:-webkit-autofill{animation:rikAutofillStart 0.001s;}";
    document.head.appendChild(s);
    return () => document.getElementById("rik-autofill-style")?.remove();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const data = await api("POST", "/auth/customer-portal/login", { email, password }, { skipAuthRedirect: true });
      onLogin(data.user, data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh", background: C.bg, display: "flex",
      alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{
        width: "100%", maxWidth: 420, background: C.card,
        borderRadius: 16, border: `1px solid ${C.border}`, padding: 36,
      }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <img src="/rik-logo.png" alt="Rik.ai" style={{ width: 64, height: 64, objectFit: "contain", margin: "0 auto 16px", display: "block" }} />
          <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 4 }}>
            <span style={{ color: C.text }}>Rik</span>
            <span style={{ background: `linear-gradient(90deg, ${C.brandPurple}, ${C.cyanBright})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>.ai</span>
          </h1>
          <p style={{ fontSize: 12, color: C.muted, letterSpacing: "0.5px" }}>
            The new verse of market intelligence
          </p>
        </div>

        <form onSubmit={handleLogin} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Input label="Email" type="email" value={email} onChange={setEmail} placeholder="you@company.com" required />
          <Input label="Password" type="password" value={password} onChange={setPassword} placeholder="••••••••" required />
          {error && <p style={{ color: C.danger, fontSize: 13 }}>{error}</p>}
          <Btn type="submit" disabled={loading} style={{ width: "100%", justifyContent: "center" }}>
            {loading ? "Signing in…" : "Sign in"}
          </Btn>
        </form>

        <p style={{ fontSize: 11, color: C.muted, textAlign: "center", marginTop: 20 }}>
          Don't have an account? Contact your Rik.ai account manager.
        </p>
        <p style={{ fontSize: 11, color: C.muted, textAlign: "center", marginTop: 12 }}>
          By signing in you agree to our{" "}
          <a href="/api/legal/customer/terms_of_service/page" target="_blank" rel="noopener" style={{ color: C.muted, textDecoration: "underline" }}>
            Terms of Service
          </a>
          {" "}&amp;{" "}
          <a href="/api/legal/customer/privacy_policy/page" target="_blank" rel="noopener" style={{ color: C.muted, textDecoration: "underline" }}>
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// EXECUTIVE DASHBOARD
// ══════════════════════════════════════════════════════════════════════════════

// RAG dot indicator
function RagDot({ status }) {
  const COLOR = { green: C.success, amber: C.warning, red: C.danger, neutral: C.muted };
  return (
    <span style={{
      display: "inline-block", width: 8, height: 8, borderRadius: "50%",
      background: COLOR[status] || C.muted, flexShrink: 0,
    }} />
  );
}

// RAG reason human-readable
function ragLabel(reason) {
  return {
    on_track:         "On track",
    slightly_behind:  "Slightly behind",
    behind_target:    "Behind target",
    stalled:          "Stalled",
    no_deadline:      "No deadline set",
    early_stage:      "Early stage",
    too_early:        "Just started",
    active:           "Active",
    draft:            "Draft",
    closed:           "Closed",
    neutral:          "–",
  }[reason] || reason;
}

// Report readiness badge
function ReadinessBadge({ readiness }) {
  const map = {
    collecting:     { label: "Collecting",     bg: "rgba(139,148,168,0.15)", color: C.muted },
    early_insights: { label: "Early Insights", bg: "rgba(79,110,247,0.15)",  color: C.accent },
    ready:          { label: "Report Ready",   bg: "rgba(16,185,129,0.15)",  color: C.success },
  };
  const s = map[readiness] || map.collecting;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
      background: s.bg, color: s.color, textTransform: "uppercase", letterSpacing: "0.5px",
    }}>{s.label}</span>
  );
}

function DashboardPage({ customer, onViewSurvey, onViewProject, onViewTicket }) {
  const { isMobile, isTablet } = useViewport();
  const [dash, setDash]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [filter, setFilter]       = useState("all");
  const [search, setSearch]       = useState("");
  const [toast, setToast]         = useState("");
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [hiddenAlertKeys, setHiddenAlertKeys] = useState(() => new Set()); // dismissed/snoozed this view
  const [snoozeMenuFor, setSnoozeMenuFor]     = useState(null);            // alert key with snooze picker open
  const [collapsedGroups, setCollapsedGroups] = useState({ info: true });    // info folded by default
  const [expandedGroups, setExpandedGroups]   = useState(() => new Set());   // per-group "show more"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api("GET", `/customers/${customer?.customerId}/dashboard`);
      setDash(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [customer?.customerId]);

  useEffect(() => { load(); }, [load]);


  const surveys  = dash?.surveys  || [];
  const alerts   = dash?.alerts   || [];
  const fin      = dash?.financials || {};

  // Prefer the server-computed stable key; fall back for older payloads/tests.
  const keyOf      = (a) => a.key || `${a.type}:${a.surveyId || a.projectId || a.ticketId || a.message}`;
  const liveAlerts = alerts.filter(a => !hiddenAlertKeys.has(keyOf(a)));
  const PER_GROUP  = 6; // soft cap per severity group before "show more"

  const ALERT_GROUPS = [
    { sev: "critical", label: "Critical — needs action now", color: C.danger,  icon: "●" },
    { sev: "warning",  label: "Warning — watch these",        color: C.warning, icon: "◆" },
    { sev: "info",     label: "Info — opportunities",          color: C.accent,  icon: "ℹ" },
  ].map(g => ({ ...g, items: liveAlerts.filter(a => a.severity === g.sev) }))
   .filter(g => g.items.length > 0);

  // Dismiss = hide for this session only (alert recomputes on next load).
  const dismissAlert = (a) => setHiddenAlertKeys(prev => new Set(prev).add(keyOf(a)));
  // Snooze = persisted server-side for `hours`; hide locally now so it disappears immediately.
  const snoozeAlert = async (a, hours) => {
    setSnoozeMenuFor(null);
    setHiddenAlertKeys(prev => new Set(prev).add(keyOf(a)));
    try {
      await api("POST", `/customers/${customer.customerId}/alerts/snooze`, {
        key: keyOf(a), severity: a.severity, durationHours: hours,
      });
    } catch {
      setToast("Couldn't snooze that alert — please try again.");
      setHiddenAlertKeys(prev => { const n = new Set(prev); n.delete(keyOf(a)); return n; }); // roll back
    }
  };
  const toggleGroup  = (sev) => setCollapsedGroups(prev => ({ ...prev, [sev]: !prev[sev] }));
  const toggleExpand = (sev) => setExpandedGroups(prev => {
    const next = new Set(prev);
    next.has(sev) ? next.delete(sev) : next.add(sev);
    return next;
  });

  // Primary action per alert — deep-links to the exact point of action.
  // Customers can only VIEW reports (the research team generates them), so
  // report alerts route to the survey's Report tab or just surface status.
  const alertAction = (a) => {
    switch (a.type) {
      case "ticket_update":
        return { label: "Open ticket", run: () => onViewTicket && onViewTicket(a.ticketId) };
      case "report_done":
        return { label: "View report", run: () => onViewSurvey(a.surveyId, "report") };
      case "report_ready":
      case "early_insights":
        return { label: "Open", run: () => onViewSurvey(a.surveyId) };
      case "project_deadline":
        return { label: "Open", run: () => onViewProject(a.projectId, "surveys") };
      case "invoice_overdue":
      case "invoice_due_soon":
        return a.projectId
          ? { label: "View invoice", run: () => onViewProject(a.projectId, "financials") }
          : null; // customer-level invoice — no in-portal billing screen yet
      default: // survey_stalled / behind / slightly_behind / closing_soon
        if (a.surveyId)  return { label: "Open", run: () => onViewSurvey(a.surveyId) };
        if (a.projectId) return { label: "Open", run: () => onViewProject(a.projectId) };
        return null;
    }
  };

  const filtered = surveys.filter((s) => {
    if (filter !== "all" && s.status !== filter) return false;
    if (search && !s.title?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const stats = {
    active:   surveys.filter(s => s.status === "active").length,
    completed:surveys.reduce((a, s) => a + (s.completedCount || 0), 0),
  };

  const greetHour = new Date().getHours();
  const greetWord = greetHour < 12 ? "Good morning" : greetHour < 18 ? "Good afternoon" : "Good evening";
  const firstName = customer.firstName || customer.company?.companyName || "there";
  const projects  = dash?.projects || [];

  const pad = isMobile ? "16px" : isTablet ? "20px 24px" : "28px 32px";

  return (
    <div style={{ padding: pad, maxWidth: 1200, margin: "0 auto" }}>
      {toast && <Toast message={toast} onDone={() => setToast("")} />}

      {/* ── Page header ── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontSize: isMobile ? 20 : 26, fontWeight: 800, color: C.text, marginBottom: 4 }}>
            {greetWord}, {firstName}
          </h2>
          <p style={{ fontSize: 12, color: C.muted }}>
            {loading ? "Loading…" : `${stats.active} active · ${stats.completed} completions`}
          </p>
        </div>
      </div>

      {error && <p style={{ color: C.danger, marginBottom: 20 }}>{error}</p>}

      {/* ── Alerts — grouped by severity, collapsible, actionable ── */}
      {(loading || alerts.length > 0) && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Alerts</div>
            {!loading && liveAlerts.length > 0 && (
              <div style={{ fontSize: 11, color: C.muted }}>
                {ALERT_GROUPS.map(g => `${g.items.length} ${g.sev}`).join(" · ")}
              </div>
            )}
          </div>
          <Card style={{ padding: 0, overflow: "hidden" }}>
            {loading && <div style={{ padding: "14px 16px", color: C.muted, fontSize: 12 }}>Loading…</div>}
            {!loading && liveAlerts.length === 0 && (
              <div style={{ padding: "16px", fontSize: 13, color: C.muted, textAlign: "center" }}>✅ No alerts right now</div>
            )}
            {!loading && ALERT_GROUPS.map((g, gi) => {
              const collapsed = !!collapsedGroups[g.sev];
              const expanded  = expandedGroups.has(g.sev);
              const shown     = expanded ? g.items : g.items.slice(0, PER_GROUP);
              const hidden    = g.items.length - shown.length;
              return (
                <div key={g.sev} style={{ borderBottom: gi === ALERT_GROUPS.length - 1 ? "none" : `1px solid ${C.border}` }}>
                  {/* Group header */}
                  <div
                    onClick={() => toggleGroup(g.sev)}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: isMobile ? "10px 14px" : "11px 16px", cursor: "pointer", userSelect: "none" }}
                    onMouseEnter={e => { e.currentTarget.style.background = C.cardHover; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <span style={{ fontSize: 10, color: C.muted, width: 10, transform: collapsed ? "rotate(-90deg)" : "none", transition: "transform 0.15s" }}>▼</span>
                    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 20, height: 20, padding: "0 6px", borderRadius: 10, fontSize: 11, fontWeight: 700, background: `${g.color}22`, color: g.color }}>{g.items.length}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: g.color, flex: 1 }}>{g.label}</span>
                  </div>
                  {/* Group body */}
                  {!collapsed && shown.map((alert) => {
                    const action      = alertAction(alert);
                    const k           = keyOf(alert);
                    const snoozeArmed = snoozeMenuFor === k;
                    const ghostBtn    = { fontSize: 11, fontWeight: 600, padding: "5px 8px", borderRadius: 7, cursor: "pointer", fontFamily: "inherit", border: `1px solid ${C.border}`, background: "transparent", color: C.muted };
                    return (
                      <div
                        key={k}
                        style={{
                          display: "flex", alignItems: "center", gap: 12,
                          padding: isMobile ? "11px 14px 11px 28px" : "11px 16px 11px 30px",
                          borderTop: `1px solid ${C.border}`,
                        }}
                      >
                        <span style={{ color: g.color, fontSize: 9, flexShrink: 0, lineHeight: 1 }}>{g.icon}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: C.text, lineHeight: 1.4 }}>{alert.message}</div>
                          {alert.subtitle && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{alert.subtitle}</div>}
                        </div>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "center" }}>
                          {snoozeArmed ? (
                            <>
                              <span style={{ fontSize: 11, color: C.muted, marginRight: 2 }}>Snooze:</span>
                              {[{ l: "1d", h: 24 }, { l: "3d", h: 72 }, { l: "7d", h: 168 }].map(opt => (
                                <button key={opt.h} onClick={(e) => { e.stopPropagation(); snoozeAlert(alert, opt.h); }} style={ghostBtn}>{opt.l}</button>
                              ))}
                              <button onClick={(e) => { e.stopPropagation(); setSnoozeMenuFor(null); }} title="Cancel" style={{ ...ghostBtn, padding: "5px 8px" }}>✕</button>
                            </>
                          ) : (
                            <>
                              {action && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); action.run(); }}
                                  style={{
                                    fontSize: 11, fontWeight: 600, padding: "5px 10px", borderRadius: 7, cursor: "pointer",
                                    fontFamily: "inherit", whiteSpace: "nowrap",
                                    border: `1px solid ${alert.severity === "info" ? C.accent : C.border}`,
                                    background: alert.severity === "info" ? C.accent : "transparent",
                                    color: alert.severity === "info" ? "#fff" : C.textDim,
                                  }}
                                >{action.label}</button>
                              )}
                              <button onClick={(e) => { e.stopPropagation(); setSnoozeMenuFor(k); }} title="Snooze" style={{ ...ghostBtn, fontSize: 13 }}>⏰</button>
                              <button onClick={(e) => { e.stopPropagation(); dismissAlert(alert); }} title="Dismiss" style={{ ...ghostBtn, fontSize: 13 }}>✕</button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {!collapsed && (hidden > 0 || expanded) && g.items.length > PER_GROUP && (
                    <div
                      onClick={() => toggleExpand(g.sev)}
                      style={{ padding: "9px 16px 9px 30px", fontSize: 12, fontWeight: 600, color: C.accent, cursor: "pointer", borderTop: `1px solid ${C.border}` }}
                    >
                      {expanded ? "Show fewer" : `Show ${hidden} more`}
                    </div>
                  )}
                </div>
              );
            })}
          </Card>
        </div>
      )}

      {/* ── Projects — dense rows with health rollup ── */}
      {(loading || projects.length > 0) && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Projects</div>
            {!loading && projects.length > 0 && (
              <div style={{ fontSize: 11, color: C.muted }}>{projects.length} total</div>
            )}
          </div>
          {loading ? (
            <div style={{ color: C.muted, fontSize: 12 }}>Loading…</div>
          ) : (() => {
            const PREVIEW = 5;
            const shown = showAllProjects ? projects : projects.slice(0, PREVIEW);
            const hidden = projects.length - shown.length;
            return (
              <Card style={{ padding: 0, overflow: "hidden" }}>
                {shown.map((p, i) => {
                  const pColor = p.status === "active" ? C.accent : p.status === "completed" ? C.success : p.status === "on_hold" ? C.warning : C.muted;
                  const projectAlertCount = alerts.filter(a => a.projectId === p.id).length;
                  const rag = p.ragRollup || { green: 0, amber: 0, red: 0 };
                  const ragTotal = rag.green + rag.amber + rag.red;
                  const isLast = i === shown.length - 1 && hidden <= 0 && !showAllProjects;
                  return (
                    <div
                      key={p.id}
                      onClick={() => onViewProject(p.id)}
                      style={{
                        display: "flex", alignItems: "center", gap: isMobile ? 10 : 14,
                        padding: isMobile ? "11px 14px" : "11px 16px",
                        borderBottom: isLast ? "none" : `1px solid ${C.border}`,
                        cursor: "pointer", transition: "background 0.15s",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = C.cardHover; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                    >
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: pColor, flexShrink: 0 }} title={p.status?.replace(/_/g, " ")} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: C.text, minWidth: 0, flex: isMobile ? 1 : "0 1 180px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {p.name}
                      </span>
                      {!isMobile && (
                        <span style={{ fontSize: 11, color: C.muted, width: 74, flexShrink: 0 }}>
                          {p.surveyCount || 0} survey{p.surveyCount === 1 ? "" : "s"}
                        </span>
                      )}
                      {/* Health bar — RAG mix of active surveys, else completion fill */}
                      <span style={{ display: "flex", height: 6, borderRadius: 3, overflow: "hidden", flex: 1, minWidth: 60, background: C.surface }}>
                        {ragTotal > 0 ? (
                          <>
                            {rag.green > 0 && <span style={{ background: C.success, width: `${(rag.green / ragTotal) * 100}%` }} />}
                            {rag.amber > 0 && <span style={{ background: C.warning, width: `${(rag.amber / ragTotal) * 100}%` }} />}
                            {rag.red   > 0 && <span style={{ background: C.danger,  width: `${(rag.red   / ragTotal) * 100}%` }} />}
                          </>
                        ) : (
                          <span style={{ background: C.accent, width: `${p.completionPercent || 0}%` }} />
                        )}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: C.textDim, width: 42, textAlign: "right", flexShrink: 0 }}>
                        {p.completionPercent || 0}%
                      </span>
                      {projectAlertCount > 0 ? (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 7px", borderRadius: 10, background: "rgba(245,158,11,0.12)", color: C.warning, flexShrink: 0, whiteSpace: "nowrap" }}>
                          {projectAlertCount} {isMobile ? "⚠" : `need${projectAlertCount === 1 ? "s" : ""} attention`}
                        </span>
                      ) : !isMobile && (
                        <span style={{ fontSize: 11, color: pColor, textTransform: "capitalize", flexShrink: 0, width: 72, textAlign: "right" }}>
                          {p.status?.replace(/_/g, " ")}
                        </span>
                      )}
                    </div>
                  );
                })}
                {(hidden > 0 || showAllProjects) && projects.length > PREVIEW && (
                  <div
                    onClick={() => setShowAllProjects(v => !v)}
                    style={{ padding: "10px 16px", fontSize: 12, fontWeight: 600, color: C.accent, textAlign: "center", cursor: "pointer", borderTop: `1px solid ${C.border}` }}
                  >
                    {showAllProjects ? "Show fewer" : `View all ${projects.length} projects →`}
                  </div>
                )}
              </Card>
            );
          })()}
        </div>
      )}

      {/* ── Financials — full-width, only when there are amounts ── */}
      {!loading && (fin.overdueAmount > 0 || fin.pendingAmount > 0) && (
        <div style={{ marginBottom: 20 }}>
          <Card style={{ padding: "12px 16px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>Financials</div>
            <div style={{ display: "flex", gap: isMobile ? 16 : 32, flexWrap: "wrap" }}>
              {fin.overdueAmount > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 12, color: C.danger }}>Overdue (action required)</span>
                  <span style={{ fontSize: 14, color: C.danger, fontWeight: 700 }}>₹{fin.overdueAmount.toLocaleString("en-IN")}</span>
                </div>
              )}
              {fin.pendingAmount > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 12, color: C.muted }}>Upcoming</span>
                  <span style={{ fontSize: 14, color: C.textDim, fontWeight: 600 }}>₹{fin.pendingAmount.toLocaleString("en-IN")}</span>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* ── Survey list header + filters ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>All Surveys</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}>
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            style={{
              background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
              color: C.text, fontSize: 12, padding: "7px 10px", outline: "none",
              width: isMobile ? 120 : 160, minHeight: 36,
            }}
          />
          {["all", "draft", "active", "closed"].map((f) => (
            <button
              key={f} onClick={() => setFilter(f)}
              style={{
                padding: "6px 10px", borderRadius: 16, fontSize: 11, fontWeight: 600, flexShrink: 0,
                cursor: "pointer", border: `1.5px solid ${filter === f ? C.accent : C.border}`,
                background: filter === f ? C.accentDim : "transparent",
                color: filter === f ? C.accent : C.muted, textTransform: "capitalize",
                fontFamily: "inherit", minHeight: 32,
              }}
            >{f}</button>
          ))}
        </div>
      </div>

      {loading && <p style={{ color: C.muted, textAlign: "center", padding: 40 }}>Loading…</p>}

      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: 48, color: C.muted }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📋</div>
          <p style={{ fontSize: 15, marginBottom: 6 }}>No surveys yet</p>
          <p style={{ fontSize: 12 }}>Surveys assigned to your account will appear here.</p>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filtered.map((survey) => {
          const ragColor = survey.ragStatus === "red" ? C.danger : survey.ragStatus === "amber" ? C.warning : survey.ragStatus === "green" ? C.success : C.border;
          return (
            <div
              key={survey.id}
              onClick={() => onViewSurvey(survey.id)}
              style={{
                background: C.card, borderRadius: 12, border: `1px solid ${C.border}`,
                borderLeft: `3px solid ${ragColor}`, padding: isMobile ? "14px 14px" : "16px 20px",
                cursor: "pointer", transition: "all 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.borderLeftColor = ragColor; e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.borderLeftColor = ragColor; e.currentTarget.style.transform = "none"; }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5, flexWrap: "wrap" }}>
                    <RagDot status={survey.ragStatus} />
                    <h3 style={{ fontSize: isMobile ? 13 : 15, fontWeight: 700, color: C.text }}>{survey.title}</h3>
                    <StatusBadge status={survey.status} />
                    <ReadinessBadge readiness={survey.reportReadiness} />
                  </div>
                  <div style={{ display: "flex", gap: 12, fontSize: 11, color: C.muted, flexWrap: "wrap", marginBottom: survey.purpose ? 6 : 0 }}>
                    <span style={{ color: ragColor }}>{ragLabel(survey.ragReason)}</span>
                    {survey.daysRemaining != null && survey.status === "active" && <span>{survey.daysRemaining}d left</span>}
                    {survey.responsesToday > 0 && <span style={{ color: C.success }}>+{survey.responsesToday} today</span>}
                  </div>
                  {survey.purpose && (
                    <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.4, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                      {survey.purpose}
                    </div>
                  )}
                </div>
                <div style={{ flexShrink: 0, textAlign: "center" }}>
                  <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 900, color: C.accent }}>{survey.completionPercent}%</div>
                  <div style={{ fontSize: 9, color: C.muted }}>{survey.completedCount}/{survey.targetSampleSize || "?"}</div>
                </div>
              </div>
              <div style={{ marginTop: 10, height: 3, background: C.surface, borderRadius: 2, overflow: "hidden" }}>
                <div style={{
                  height: "100%", width: `${survey.completionPercent}%`,
                  background: ragColor === C.border ? `linear-gradient(90deg, ${C.accent}, ${C.cyan})` : ragColor,
                  borderRadius: 2, transition: "width 0.6s",
                }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// CREATE SURVEY PAGE — 4-step wizard (ported from console SurveyWizard)
// ══════════════════════════════════════════════════════════════════════════════
// Category = research DOMAIN (organization only). Methodologies live in SURVEY_TYPES.
const CATEGORIES = ["Brand","Product","Customer Experience","Advertising","Innovation","Market Understanding","UX / Design","Employee","Political","Other"];
const WIZ_AGENTS = ["jal","vayu","agni","prithvi","aakash"];
const AGENT_META = {
  jal:     { name:"Jal",     element:"Water", color:"#06B6D4", mode:"Empathy Mode",    tagline:"Rapport Building",  bestFor:["Consumer Emotion","Healthcare","Financial Stress","Life Decisions"] },
  vayu:    { name:"Vayu",    element:"Air",   color:"#A78BFA", mode:"Discovery Mode",  tagline:"Open Exploration",  bestFor:["Unaided Awareness","New Categories","Early Concept Testing","Exploratory"] },
  agni:    { name:"Agni",    element:"Fire",  color:"#F59E0B", mode:"Insight Mode",    tagline:"Depth Probing",     bestFor:["Brand Loyalty","Competitor Analysis","NPS","Product Pain Points"] },
  prithvi: { name:"Prithvi", element:"Earth", color:"#22C55E", mode:"Clarity Mode",    tagline:"Validation",        bestFor:["Usage Habits","Purchase Behaviour","Satisfaction Scoring","Claims Testing"] },
  aakash:  { name:"Aakash",  element:"Space", color:"#6366F1", mode:"Reflection Mode", tagline:"Deep Thinking",     bestFor:["Future Trends","Innovation","Life Stage Transitions","Long-horizon Decisions"] },
};
const LANGUAGE_BCP47_MAP = {
  "English":"en-US","Hindi":"hi-IN","Tamil":"ta-IN","Telugu":"te-IN",
  "Marathi":"mr-IN","Bengali":"bn-IN","Gujarati":"gu-IN","Kannada":"kn-IN",
  "Malayalam":"ml-IN","Punjabi":"pa-IN","Other":"en-US",
};
const WIZ_LANGUAGES = Object.keys(LANGUAGE_BCP47_MAP);
const MODALITIES = [
  { value: "video", label: "Video", desc: "Camera + mic. Transcript, sentiment, facial analysis." },
  { value: "voice", label: "Voice", desc: "Mic only. Transcript + sentiment." },
  { value: "chat",  label: "Chat",  desc: "Text only. No audio or video." },
];
// Survey Type = METHODOLOGY — mirrors api/config/studyTypes.js (drives the compiler + report).
const SURVEY_TYPES = [
  { value:"nps",              label:"Net Promoter Score (NPS)" },
  { value:"csat",             label:"Customer Satisfaction (CSAT)" },
  { value:"ces",              label:"Customer Effort Score (CES)" },
  { value:"brand_tracking",   label:"Brand Tracking" },
  { value:"product_feedback", label:"Product Feedback" },
  { value:"concept_testing",  label:"Concept Testing" },
  { value:"ad_testing",       label:"Ad Testing" },
  { value:"market_sizing",    label:"Market Sizing (U&A)" },
  { value:"ux_research",      label:"UX Research" },
  { value:"custom",           label:"Custom / Exploratory" },
];
const QUESTION_TYPES = [
  { value: "open",         label: "Open-ended" },
  { value: "rating_scale", label: "Rating (1-5)" },
  { value: "binary",       label: "Yes / No" },
  { value: "ranking",      label: "Ranking" },
];
const INCENTIVE_TYPES = [
  { value: "none",       label: "None" },
  { value: "cash",       label: "Cash (UPI)" },
  { value: "voucher",    label: "Voucher" },
  { value: "points",     label: "Points" },
  { value: "lucky_draw", label: "Lucky Draw" },
];
const AGE_BANDS       = ["18–24","25–34","35–44","45–54","55–64","65+"];
const GENDERS         = ["Male","Female","Non-binary","Prefer not to say"];
const CITY_TIERS      = ["Tier 1","Tier 2","Tier 3","Rural"];
const EDUCATION       = ["High School or below","Diploma / Vocational","Graduate (Bachelor's)","Post Graduate (Master's)","Doctorate / PhD","Other"];
const INCOME_BANDS    = ["Prefer not to say","Below ₹3 LPA","₹3–6 LPA","₹6–10 LPA","₹10–20 LPA","₹20–40 LPA","Above ₹40 LPA"];
const PROFESSIONS     = ["Student","Salaried — Private Sector","Salaried — Government / PSU","Business Owner / Self Employed","Freelancer / Consultant","Homemaker","Retired","Healthcare Professional","IT / Tech Professional","Finance / Banking","Other"];
const HOUSEHOLD_ROLES = ["Primary decision maker","Joint decision maker","Influencer (input but not final call)","Not involved in household decisions"];
const YEARS_WORKING   = ["Less than 1 year","1–3 years","3–7 years","7–15 years","15+ years","Not applicable"];
const INDUSTRIES      = ["FMCG / Consumer Goods","Automobile","Financial Services","Healthcare / Pharma","Real Estate","EdTech / Education","Retail / E-commerce","Travel & Hospitality","Technology / SaaS","Media & Entertainment","Agriculture","Manufacturing","None in particular"];
const SURVEY_TEMPLATES = [
  { id:"nps", label:"NPS", category:"Customer Experience", surveyType:"nps", estimatedMins:5, questions:[
    {questionText:"On a scale of 0–10, how likely are you to recommend us to a friend or colleague?",questionType:"rating_scale",helpText:"Probe for reasons behind the score."},
    {questionText:"What is the primary reason for your score?",questionType:"open",helpText:""},
    {questionText:"What could we do to improve your experience?",questionType:"open",helpText:""},
  ]},
  { id:"brand_health", label:"Brand Health", category:"Brand", surveyType:"brand_tracking", estimatedMins:10, questions:[
    {questionText:"Which brands in this category are you aware of?",questionType:"open",helpText:"Explore spontaneous brand recall."},
    {questionText:"Which of these brands have you used in the last 3 months?",questionType:"open",helpText:""},
    {questionText:"How would you describe your overall impression of [Brand]?",questionType:"open",helpText:""},
    {questionText:"What attributes do you associate with [Brand]?",questionType:"open",helpText:""},
  ]},
  { id:"product_feedback", label:"Product Feedback", category:"Product", surveyType:"product_feedback", estimatedMins:10, questions:[
    {questionText:"How long have you been using this product?",questionType:"open",helpText:""},
    {questionText:"What do you like most about the product?",questionType:"open",helpText:"Probe for specific features."},
    {questionText:"What are the biggest pain points or frustrations?",questionType:"open",helpText:""},
    {questionText:"What features would you like to see added?",questionType:"open",helpText:""},
  ]},
  { id:"concept_testing", label:"Concept Testing", category:"Innovation", surveyType:"concept_testing", estimatedMins:12, questions:[
    {questionText:"What is your first reaction to this concept?",questionType:"open",helpText:"Allow unfiltered response first."},
    {questionText:"What do you like about this concept?",questionType:"open",helpText:""},
    {questionText:"What concerns or questions does this raise for you?",questionType:"open",helpText:""},
    {questionText:"How likely would you be to use or buy this if it were available?",questionType:"rating_scale",helpText:""},
  ]},
  { id:"csat", label:"CSAT", category:"Customer Experience", surveyType:"csat", estimatedMins:7, questions:[
    {questionText:"How satisfied are you with your recent experience?",questionType:"rating_scale",helpText:""},
    {questionText:"What went well?",questionType:"open",helpText:""},
    {questionText:"What could have been better?",questionType:"open",helpText:""},
  ]},
  { id:"pricing_vw", label:"Pricing — Van Westendorp", category:"Product", surveyType:"pricing_vw", estimatedMins:8, questions:[
    {questionText:"At what price would this be so cheap you'd doubt its quality?",questionType:"open",helpText:"Capture the ₹ amount (too cheap)."},
    {questionText:"At what price would this feel like a bargain — great value for money?",questionType:"open",helpText:"Capture the ₹ amount (cheap / good value)."},
    {questionText:"At what price would this start to feel expensive?",questionType:"open",helpText:"Capture the ₹ amount (expensive)."},
    {questionText:"At what price would this be so expensive you would not buy it?",questionType:"open",helpText:"Capture the ₹ amount (too expensive)."},
    {questionText:"What shapes your sense of a fair price for this?",questionType:"open",helpText:""},
  ]},
  { id:"pricing_gg", label:"Pricing — Gabor-Granger", category:"Product", surveyType:"pricing_gg", estimatedMins:6, questions:[
    {questionText:"What is the most you would be willing to pay for this?",questionType:"open",helpText:"Capture the maximum ₹ amount (willingness to pay)."},
    {questionText:"Why does that price feel right to you?",questionType:"open",helpText:""},
  ]},
];
const defaultQ = () => ({ id: Date.now() + Math.random(), questionText: "", questionType: "open", helpText: "", isScreener: false, screenerCondition: "", skipLogic: [], measurementRole: null, measureKey: null, scaleMin: null, scaleMax: null, choices: null, isRequiredRole: false, linkedToOrderIndex: null });

function WizChips({ label, options, value = [], onChange }) {
  const toggle = (o) => onChange(value.includes(o) ? value.filter((v) => v !== o) : [...value, o]);
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: 0.8, marginBottom: 6, textTransform: "uppercase" }}>{label}</label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {options.map((o) => {
          const sel = value.includes(o);
          return <button key={o} type="button" onClick={() => toggle(o)} style={{ padding: "4px 10px", borderRadius: 99, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", border: `1px solid ${sel ? C.purple : C.border}`, background: sel ? C.accentDim : "transparent", color: sel ? C.purple : C.muted }}>{o}</button>;
        })}
      </div>
    </div>
  );
}

function CreateSurveyPage({ customer, onBack, onSuccess, prefill }) {
  const editId     = prefill?.editId || null;
  const surveyStatus = prefill?.status || "draft";
  const isPendingReview = surveyStatus === "pending_review";
  const STEPS = ["Basic Info", "Questions", "Targeting", "Review"];
  const iStyle = { width:"100%", padding:"9px 11px", background:C.surface, border:`1px solid ${C.border}`, borderRadius:6, color:C.text, fontSize:13, fontFamily:"inherit", boxSizing:"border-box", outline:"none" };
  const taStyle = { ...iStyle, resize:"vertical" };
  const lStyle  = { display:"block", fontSize:11, fontWeight:700, color:C.muted, letterSpacing:0.8, marginBottom:5, textTransform:"uppercase" };

  const [step, setStep]     = useState(0);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false); // synchronous double-submit guard (disabled={saving} only applies after re-render)
  const [error, setError]   = useState("");
  const [platformMultilingual, setPlatformMultilingual] = useState(false);
  useEffect(() => {
    api("GET", "/config").then(d => {
      if (d?.multilingualSupport !== undefined) setPlatformMultilingual(d.multilingualSupport);
    }).catch(() => {});
  }, []); // eslint-disable-line

  // Approval trail state
  const [approvalEvents,  setApprovalEvents]  = useState([]);
  const [trailLoading,    setTrailLoading]    = useState(false);
  const [approving,       setApproving]       = useState(false);
  const [rejecting,       setRejecting]       = useState(false);
  const [approvalMsg,     setApprovalMsg]     = useState("");
  const [rejectNote,      setRejectNote]      = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [currentStatus,   setCurrentStatus]   = useState(surveyStatus);

  useEffect(() => {
    if (!editId) return;
    setTrailLoading(true);
    fetch(`/api/surveys/${editId}/approval-trail`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setApprovalEvents(d.events || []); })
      .catch(() => {})
      .finally(() => setTrailLoading(false));
  }, [editId]);

  const handleApprove = async () => {
    setApproving(true); setApprovalMsg("");
    try {
      const r = await fetch(`/api/surveys/${editId}/approve`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
      if (!r.ok) { const d = await r.json(); setApprovalMsg(d.error || "Failed."); return; }
      setCurrentStatus("approved");
      setApprovalMsg("Survey approved. The admin can now publish it.");
      setApprovalEvents(prev => [...prev, { id: Date.now(), event: "approved", actorType: "customer", actorName: customer.companyName || "You", createdAt: new Date().toISOString() }]);
    } catch { setApprovalMsg("Network error."); }
    finally { setApproving(false); }
  };

  const handleReject = async () => {
    setRejecting(true); setApprovalMsg("");
    try {
      const r = await fetch(`/api/surveys/${editId}/reject`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ note: rejectNote }) });
      if (!r.ok) { const d = await r.json(); setApprovalMsg(d.error || "Failed."); return; }
      setCurrentStatus("draft");
      setApprovalMsg("Survey sent back for revision. The admin will be notified.");
      setApprovalEvents(prev => [...prev, { id: Date.now(), event: "rejected", actorType: "customer", actorName: customer.companyName || "You", note: rejectNote, createdAt: new Date().toISOString() }]);
      setShowRejectInput(false); setRejectNote("");
    } catch { setApprovalMsg("Network error."); }
    finally { setRejecting(false); }
  };

  const [basic, setBasic] = useState({
    title: prefill?.title || "", category: prefill?.category || "Brand",
    surveyType: prefill?.surveyType || "nps", agent: prefill?.agent || "jal",
    modality: prefill?.modality || "voice", multilingualEnabled: prefill?.multilingualEnabled || false,
    estimatedMins: prefill?.estimatedMins || 10, description: prefill?.description || "",
    purpose: prefill?.purpose || "", instructions: prefill?.instructions || "",
  });

  const [questions, setQuestions] = useState(() =>
    prefill?.questions?.length
      ? prefill.questions.map((q, i) => ({ ...defaultQ(), id: Date.now() + i, ...q, skipLogic: q.skipLogic || [] }))
      : [defaultQ()]
  );

  const [targeting, setTargeting] = useState({
    targetSampleSize: prefill?.targetSampleSize != null ? String(prefill.targetSampleSize) : "",
    closeDate:        prefill?.closeDate ? prefill.closeDate.split("T")[0] : "",
    incentiveType:    prefill?.incentiveType || "none",
    incentiveValue:   prefill?.incentiveValue || "",
    segmentQuotas:    prefill?.segmentQuotas || [],
    targetAgeBands:   prefill?.targetAgeBands || [],   targetGenders:    prefill?.targetGenders || [],
    targetCityTiers:  prefill?.targetCityTiers || [],
    targetEducation:  prefill?.targetEducation || [],  targetIncomeBands: prefill?.targetIncomeBands || [],
    targetProfessions: prefill?.targetProfessions || [], targetHouseholdRoles: prefill?.targetHouseholdRoles || [],
    targetYearsWorking: prefill?.targetYearsWorking || [], targetIndustries: prefill?.targetIndustries || [],
  });

  // Apply template questions when selected in step 0
  useEffect(() => {
    if (basic._templateQuestions) {
      setQuestions(basic._templateQuestions.map((q, i) => ({ ...defaultQ(), id: Date.now() + i, ...q })));
      setBasic((b) => { const nb = { ...b }; delete nb._templateQuestions; return nb; });
    }
  }, [basic._templateQuestions]); // eslint-disable-line

  // Question helpers
  const addQ    = () => setQuestions((qs) => [...qs, defaultQ()]);
  const removeQ = (id) => setQuestions((qs) => qs.length > 1 ? qs.filter((q) => q.id !== id) : qs);
  const updateQ = (id, field, val) => setQuestions((qs) => qs.map((q) => q.id === id ? { ...q, [field]: val } : q));
  const moveQ   = (idx, dir) => setQuestions((qs) => { const a = [...qs]; [a[idx], a[idx + dir]] = [a[idx + dir], a[idx]]; return a; });
  const addSkip = (id) => setQuestions((qs) => qs.map((q) => q.id === id && (q.skipLogic || []).length < 3 ? { ...q, skipLogic: [...q.skipLogic, { condition: "", skipToIndex: -1 }] } : q));
  const updSkip = (id, ri, f, v) => setQuestions((qs) => qs.map((q) => q.id === id ? { ...q, skipLogic: q.skipLogic.map((r, i) => i === ri ? { ...r, [f]: v } : r) } : q));
  const remSkip = (id, ri) => setQuestions((qs) => qs.map((q) => q.id === id ? { ...q, skipLogic: q.skipLogic.filter((_, i) => i !== ri) } : q));


  const applyTemplate = (t) => {
    setBasic((b) => ({ ...b, category:t.category, surveyType:t.surveyType, estimatedMins:t.estimatedMins, _templateQuestions:t.questions }));
  };

  // Note: the AI Study Compiler ("Generate with AI") lives in the console wizard
  // only. The customer portal is for reviewing and making small edits.

  // Segment quota state
  const [newQuota, setNewQuota] = useState({ dimension:"Gender", value:"", quota:"" });
  const addQuota    = () => { if (!newQuota.value || !newQuota.quota) return; setTargeting((t) => ({ ...t, segmentQuotas:[...(t.segmentQuotas||[]),{...newQuota,quota:Number(newQuota.quota)}] })); setNewQuota({dimension:"Gender",value:"",quota:""}); };
  const removeQuota = (i) => setTargeting((t) => ({ ...t, segmentQuotas:(t.segmentQuotas||[]).filter((_,j)=>j!==i) }));
  const setT        = (field, val) => setTargeting((t) => ({ ...t, [field]: val }));

  // Save
  const buildPayload = () => ({
    customerId: customer.customerId, ...basic,
    estimatedMins: Number(basic.estimatedMins)||10, ...targeting,
    targetSampleSize: targeting.targetSampleSize ? Number(targeting.targetSampleSize) : null,
    incentiveType: targeting.incentiveType !== "none" ? targeting.incentiveType : null,
    incentiveValue: targeting.incentiveValue || null, closeDate: targeting.closeDate || null,
    questions: questions.map((q) => ({ questionText:q.questionText, questionType:q.questionType, helpText:q.helpText||null, isScreener:q.isScreener||false, screenerCondition:q.screenerCondition||null, skipLogic:(q.skipLogic||[]).filter((r)=>r.condition?.trim()), measurementRole:q.measurementRole||null, measureKey:q.measureKey||null, scaleMin:q.scaleMin??null, scaleMax:q.scaleMax??null, choices:q.choices||null, isRequiredRole:!!q.isRequiredRole, linkedToOrderIndex:q.linkedToOrderIndex??null })),
  });

  const validate = () => {
    if (!basic.title.trim()) { setError("Survey title is required."); setStep(0); return false; }
    if (questions.some((q) => !q.questionText.trim())) { setError("All questions must have text."); setStep(1); return false; }
    return true;
  };

  const handleSave = async (mode = "draft") => {
    if (!validate()) return;
    if (savingRef.current) return; // block rapid double-submit before re-render disables the button
    savingRef.current = true;
    setSaving(true); setError("");
    try {
      const payload = buildPayload();
      let survey;
      if (editId) { const r = await api("PATCH", `/surveys/${editId}`, payload); survey = r.survey; }
      else         { const r = await api("POST", "/surveys", payload);            survey = r.survey; }
      if (mode === "publish") await api("POST", `/surveys/${survey.id}/publish`, {});
      onSuccess(survey.id);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); savingRef.current = false; }
  };

  // Once approved/active/closed the survey is frozen — no further customer edits.
  const locked = ["approved", "active", "closed"].includes(currentStatus);
  const onLastStep = step === STEPS.length - 1;

  return (
    <div style={{ padding: "24px", maxWidth: 880, margin: "0 auto" }}>
      <button onClick={onBack} style={{ background:"transparent", border:"none", color:C.muted, cursor:"pointer", fontSize:13, marginBottom:16, fontFamily:"inherit" }}>← Back</button>
      <h2 style={{ fontSize:24, fontWeight:800, color:C.text, marginBottom:4 }}>{editId ? "Edit Survey" : "New Survey"}</h2>
      <p style={{ fontSize:13, color:C.muted, marginBottom:20 }}>Step {step + 1} of {STEPS.length}: {STEPS[step]}</p>

      {/* Step tabs */}
      <div style={{ display:"flex", gap:0, marginBottom:24, borderBottom:`1px solid ${C.border}` }}>
        {STEPS.map((s, i) => (
          <button key={s} onClick={() => i < step && setStep(i)} style={{ background:"none", border:"none", cursor:i < step ? "pointer" : "default", padding:"8px 14px", fontSize:12, fontWeight:600, fontFamily:"inherit", color:i === step ? C.purple : i < step ? C.text : C.muted, borderBottom:`2px solid ${i === step ? C.purple : "transparent"}`, whiteSpace:"nowrap" }}>
            {i < step ? "✓ " : ""}{s}
          </button>
        ))}
      </div>

      {error && <div style={{ color:C.danger, fontSize:13, marginBottom:16, padding:10, background:"#EF444422", borderRadius:6, border:`1px solid ${C.danger}` }}>{error}</div>}

      {/* All editable controls live inside this fieldset — disabled once the
          survey is locked (approved/active/closed) so it can be viewed but not changed. */}
      <fieldset disabled={locked} style={{ border:"none", padding:0, margin:0, minWidth:0 }}>

      {/* ══ STEP 0: Basic Info ══ */}
      {step === 0 && (
        <Card>

          <div style={{ marginBottom:14 }}>
            <label style={lStyle}>Survey Title <span style={{ color:C.danger }}>*</span></label>
            <input style={iStyle} value={basic.title} onChange={(e) => setBasic((b) => ({ ...b, title:e.target.value }))} placeholder="e.g. Brand Awareness Study Q1 2026" />
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
            <div>
              <label style={lStyle}>Category</label>
              <select style={iStyle} value={basic.category} onChange={(e) => setBasic((b) => ({ ...b, category:e.target.value }))}>
                {!CATEGORIES.includes(basic.category) && basic.category && <option>{basic.category}</option>}
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={lStyle}>Survey Type</label>
              <select style={iStyle} value={basic.surveyType} onChange={(e) => setBasic((b) => ({ ...b, surveyType:e.target.value }))}>
                {!SURVEY_TYPES.some((t) => t.value === basic.surveyType) && basic.surveyType && <option value={basic.surveyType}>{basic.surveyType.replace(/_/g, " ")}</option>}
                {SURVEY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>

          {/* Agent card grid */}
          <div style={{ marginBottom:14 }}>
            <label style={lStyle}>AI Interviewer Agent <span style={{ color:C.danger }}>*</span></label>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginTop:4 }}>
              {WIZ_AGENTS.map((key) => {
                const m = AGENT_META[key];
                const sel = (basic.agent || "jal") === key;
                return (
                  <div key={key} onClick={() => setBasic((b) => ({ ...b, agent:key }))} style={{ background:sel ? `${m.color}18` : C.surface, border:`2px solid ${sel ? m.color : C.border}`, borderRadius:10, padding:"12px 10px", cursor:"pointer", position:"relative", boxShadow:sel ? `0 0 12px ${m.color}44` : "none" }}>
                    {sel && <div style={{ position:"absolute", top:6, right:6, fontSize:9, fontWeight:700, color:m.color, background:`${m.color}22`, borderRadius:99, padding:"1px 5px" }}>✓</div>}
                    <div style={{ fontWeight:700, fontSize:13, color:m.color, marginBottom:4 }}>{m.name}</div>
                    <div style={{ fontSize:10, fontWeight:700, color:C.text, marginBottom:3 }}>{m.mode}</div>
                    <div style={{ fontSize:9, color:C.muted, marginBottom:4, fontStyle:"italic" }}>{m.tagline}</div>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:3 }}>
                      {m.bestFor.map((tag) => <span key={tag} style={{ fontSize:9, padding:"2px 5px", borderRadius:99, background:sel ? `${m.color}22` : C.card, color:sel ? m.color : C.muted, fontWeight:600 }}>{tag}</span>)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:14 }}>
            <div>
              <label style={lStyle}>Modality</label>
              <select style={iStyle} value={basic.modality} onChange={(e) => setBasic((b) => ({ ...b, modality:e.target.value }))}>
                {MODALITIES.map((m) => <option key={m.value} value={m.value}>{m.label} — {m.desc}</option>)}
              </select>
            </div>
            <div>
              <label style={lStyle}>Multilingual</label>
              <div style={{display:"flex",alignItems:"center",gap:10,height:38}}>
                <button
                  type="button"
                  onClick={()=>platformMultilingual&&setBasic(b=>({...b,multilingualEnabled:!b.multilingualEnabled}))}
                  disabled={!platformMultilingual}
                  style={{
                    width:44,height:24,borderRadius:12,border:"none",
                    cursor:platformMultilingual?"pointer":"not-allowed",
                    background:basic.multilingualEnabled&&platformMultilingual?"#22C55E":"#374151",
                    position:"relative",transition:"background 0.2s",flexShrink:0,
                  }}
                >
                  <span style={{
                    position:"absolute",top:3,
                    left:basic.multilingualEnabled&&platformMultilingual?22:3,
                    width:18,height:18,borderRadius:"50%",background:"#fff",transition:"left 0.2s",
                  }}/>
                </button>
                <span style={{fontSize:12,color:!platformMultilingual?C.muted:basic.multilingualEnabled?"#22C55E":C.muted}}>
                  {!platformMultilingual?"English only":basic.multilingualEnabled?"Participant's language":"English only"}
                </span>
              </div>
            </div>
            <div>
              <label style={lStyle}>Duration (minutes)</label>
              <input style={iStyle} type="number" min="1" max="60" value={basic.estimatedMins} onChange={(e) => setBasic((b) => ({ ...b, estimatedMins:e.target.value }))} />
            </div>
          </div>

          <div style={{ marginBottom:14 }}>
            <label style={lStyle}>Description <span style={{ color:C.danger }}>*</span></label>
            <textarea style={taStyle} rows={3} value={basic.description} onChange={(e) => setBasic((b) => ({ ...b, description:e.target.value }))} placeholder="Brief overview shown to participants…" />
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={lStyle}>Research Purpose <span style={{ color:C.danger }}>*</span></label>
            <textarea style={taStyle} rows={2} value={basic.purpose} onChange={(e) => setBasic((b) => ({ ...b, purpose:e.target.value }))} placeholder="Internal objective — guides the AI agent's probing strategy…" />
          </div>
          <div>
            <label style={lStyle}>Pre-interview Instructions</label>
            <textarea style={taStyle} rows={2} value={basic.instructions} onChange={(e) => setBasic((b) => ({ ...b, instructions:e.target.value }))} placeholder="Shown to participant on setup screen before interview begins…" />
          </div>
        </Card>
      )}

      {/* ══ STEP 1: Questions ══ */}
      {step === 1 && (
        <div>
          {questions.map((q, idx) => (
            <div key={q.id} style={{ background:C.surface, border:`1px solid ${q.isRequiredRole ? C.purple : q.isScreener ? C.gold : C.border}`, borderRadius:10, padding:16, marginBottom:12 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10, flexWrap:"wrap" }}>
                <span style={{ background:C.bg, color:C.purple, fontWeight:700, fontSize:12, padding:"2px 8px", borderRadius:99 }}>Q{idx + 1}</span>
                {q.isRequiredRole && <span title="Required for your study type's metric" style={{ background:"rgba(167,139,250,0.15)", color:C.purple, fontWeight:700, fontSize:10, padding:"2px 8px", borderRadius:99, textTransform:"uppercase", letterSpacing:0.3 }}>🔒 Required · {(q.measureKey || q.measurementRole || "").replace(/_/g," ")}</span>}
                {q.stimulusNeed && <span title="This question benefits from showing a stimulus — attach an image or video below" style={{ background:q.stimulusNeed==="required"?"rgba(245,158,11,0.18)":"rgba(148,163,184,0.15)", color:q.stimulusNeed==="required"?C.gold:C.muted, fontWeight:700, fontSize:10, padding:"2px 8px", borderRadius:99, textTransform:"uppercase", letterSpacing:0.3 }}>📎 Stimulus {q.stimulusNeed}</span>}
                <select style={{ ...iStyle, flex:1, maxWidth:160 }} value={q.questionType} onChange={(e) => updateQ(q.id, "questionType", e.target.value)}>
                  {QUESTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <label style={{ display:"flex", alignItems:"center", gap:5, cursor:"pointer", fontSize:12, color:q.isScreener ? C.gold : C.muted }}>
                  <input type="checkbox" checked={q.isScreener} onChange={(e) => updateQ(q.id, "isScreener", e.target.checked)} /> Screener
                </label>
                <div style={{ marginLeft:"auto", display:"flex", gap:4 }}>
                  {idx > 0 && <button type="button" onClick={() => moveQ(idx, -1)} style={{ background:"transparent", border:`1px solid ${C.border}`, borderRadius:4, color:C.muted, cursor:"pointer", padding:"3px 8px", fontFamily:"inherit" }}>↑</button>}
                  {idx < questions.length - 1 && <button type="button" onClick={() => moveQ(idx, 1)} style={{ background:"transparent", border:`1px solid ${C.border}`, borderRadius:4, color:C.muted, cursor:"pointer", padding:"3px 8px", fontFamily:"inherit" }}>↓</button>}
                  <button type="button" onClick={() => { if (q.isRequiredRole && !window.confirm("This question is required for your study type's metric — removing it may block publishing. Remove anyway?")) return; removeQ(q.id); }} style={{ background:"transparent", border:`1px solid ${C.danger}`, borderRadius:4, color:C.danger, cursor:"pointer", padding:"3px 8px", fontFamily:"inherit" }}>×</button>
                </div>
              </div>
              <div style={{ marginBottom:10 }}>
                <label style={lStyle}>Question Text <span style={{ color:C.danger }}>*</span></label>
                <textarea style={taStyle} rows={2} value={q.questionText} onChange={(e) => updateQ(q.id, "questionText", e.target.value)} placeholder="Enter your question…" />
              </div>
              <div style={{ marginBottom:10 }}>
                <label style={lStyle}>AI Probing Hint (internal)</label>
                <input style={iStyle} value={q.helpText} onChange={(e) => updateQ(q.id, "helpText", e.target.value)} placeholder="e.g. Probe for specific brands mentioned…" />
              </div>
              {q.isScreener && (
                <div style={{ marginBottom:10 }}>
                  <label style={lStyle}>Screener Pass Condition</label>
                  <input style={iStyle} value={q.screenerCondition} onChange={(e) => updateQ(q.id, "screenerCondition", e.target.value)} placeholder="e.g. Pass if participant mentions owning a car" />
                </div>
              )}
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:C.muted, letterSpacing:0.8, marginBottom:6, textTransform:"uppercase" }}>Skip Logic</div>
                {(q.skipLogic || []).map((rule, ri) => (
                  <div key={ri} style={{ display:"flex", gap:8, alignItems:"center", marginBottom:6 }}>
                    <span style={{ fontSize:12, color:C.muted }}>If answer is</span>
                    <input style={{ ...iStyle, flex:1 }} placeholder='e.g. "No"' value={rule.condition} onChange={(e) => updSkip(q.id, ri, "condition", e.target.value)} />
                    <span style={{ fontSize:12, color:C.muted }}>→ skip to</span>
                    <select style={{ ...iStyle, width:120 }} value={rule.skipToIndex} onChange={(e) => updSkip(q.id, ri, "skipToIndex", Number(e.target.value))}>
                      <option value={-1}>End survey</option>
                      {questions.map((tq, ti) => ti > idx && <option key={ti} value={ti}>Q{ti + 1}</option>)}
                    </select>
                    <button type="button" onClick={() => remSkip(q.id, ri)} style={{ background:"transparent", border:`1px solid ${C.danger}`, borderRadius:4, color:C.danger, cursor:"pointer", padding:"3px 8px", fontFamily:"inherit" }}>×</button>
                  </div>
                ))}
                {(q.skipLogic || []).length < 3 && (
                  <button type="button" onClick={() => addSkip(q.id)} style={{ background:"transparent", border:`1px dashed ${C.border}`, borderRadius:4, color:C.muted, cursor:"pointer", padding:"4px 10px", fontFamily:"inherit", fontSize:12 }}>+ Skip Rule</button>
                )}
              </div>
            </div>
          ))}
          <Btn variant="outline" onClick={addQ}>+ Add Question</Btn>
        </div>
      )}

      {/* ══ STEP 2: Targeting ══ */}
      {step === 2 && (
        <Card>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:14 }}>
            <div>
              <label style={lStyle}>Target Sample Size</label>
              <input style={iStyle} type="number" min="1" value={targeting.targetSampleSize} onChange={(e) => setT("targetSampleSize", e.target.value)} placeholder="e.g. 100" />
            </div>
            <div>
              <label style={lStyle}>Close Date</label>
              <input style={{ ...iStyle, colorScheme:"dark" }} type="date" value={targeting.closeDate} onChange={(e) => setT("closeDate", e.target.value)} />
            </div>
            <div>
              <label style={lStyle}>Incentive Type</label>
              <select style={iStyle} value={targeting.incentiveType} onChange={(e) => setT("incentiveType", e.target.value)}>
                {INCENTIVE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
          {targeting.incentiveType !== "none" && (
            <div style={{ marginBottom:14 }}>
              <label style={lStyle}>Incentive Value</label>
              <input style={iStyle} value={targeting.incentiveValue} onChange={(e) => setT("incentiveValue", e.target.value)} placeholder="e.g. ₹200 or Amazon voucher" />
            </div>
          )}
          <div style={{ fontSize:13, fontWeight:700, color:C.text, margin:"16px 0 10px" }}>Demographics</div>
          <WizChips label="Age Bands"  options={AGE_BANDS}     value={targeting.targetAgeBands}   onChange={(v) => setT("targetAgeBands", v)} />
          <WizChips label="Gender"     options={GENDERS}       value={targeting.targetGenders}    onChange={(v) => setT("targetGenders", v)} />
          <WizChips label="City Tier"  options={CITY_TIERS}    value={targeting.targetCityTiers}  onChange={(v) => setT("targetCityTiers", v)} />
          <div style={{ fontSize:13, fontWeight:700, color:C.text, margin:"16px 0 10px" }}>Profile Filters</div>
          <WizChips label="Education"      options={EDUCATION}       value={targeting.targetEducation}      onChange={(v) => setT("targetEducation", v)} />
          <WizChips label="Income Band"    options={INCOME_BANDS}    value={targeting.targetIncomeBands}    onChange={(v) => setT("targetIncomeBands", v)} />
          <WizChips label="Profession"     options={PROFESSIONS}     value={targeting.targetProfessions}    onChange={(v) => setT("targetProfessions", v)} />
          <WizChips label="Household Role" options={HOUSEHOLD_ROLES} value={targeting.targetHouseholdRoles} onChange={(v) => setT("targetHouseholdRoles", v)} />
          <WizChips label="Years Working"  options={YEARS_WORKING}   value={targeting.targetYearsWorking}   onChange={(v) => setT("targetYearsWorking", v)} />
          <WizChips label="Industries"     options={INDUSTRIES}      value={targeting.targetIndustries}     onChange={(v) => setT("targetIndustries", v)} />
          <div style={{ fontSize:13, fontWeight:700, color:C.text, margin:"16px 0 10px" }}>Segment Quotas</div>
          <div style={{ display:"flex", gap:8, alignItems:"flex-end", marginBottom:10 }}>
            <div style={{ flex:1 }}><label style={lStyle}>Dimension</label>
              <select style={iStyle} value={newQuota.dimension} onChange={(e) => setNewQuota((q) => ({ ...q, dimension:e.target.value }))}>
                {["Gender","Age Band","City Tier","Education","Income Band","Profession","Household Role","Language"].map((d) => <option key={d}>{d}</option>)}
              </select>
            </div>
            <div style={{ flex:2 }}><label style={lStyle}>Value</label><input style={iStyle} value={newQuota.value} onChange={(e) => setNewQuota((q) => ({ ...q, value:e.target.value }))} placeholder='e.g. "Male"' /></div>
            <div style={{ flex:1 }}><label style={lStyle}>Cap</label><input style={iStyle} type="number" min="1" value={newQuota.quota} onChange={(e) => setNewQuota((q) => ({ ...q, quota:e.target.value }))} /></div>
            <Btn onClick={addQuota} style={{ marginBottom:2 }}>Add</Btn>
          </div>
          {(targeting.segmentQuotas || []).map((q, i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:8, background:C.surface, border:`1px solid ${C.border}`, borderRadius:6, padding:"6px 10px", marginBottom:6 }}>
              <span style={{ fontSize:12, color:C.muted }}>{q.dimension}</span>
              <span style={{ fontSize:13, color:C.text, fontWeight:600 }}>{q.value}</span>
              <span style={{ marginLeft:"auto", fontSize:12, color:C.purple }}>{q.quota} max</span>
              <button type="button" onClick={() => removeQuota(i)} style={{ background:"transparent", border:`1px solid ${C.danger}`, borderRadius:4, color:C.danger, cursor:"pointer", padding:"2px 7px", fontFamily:"inherit" }}>×</button>
            </div>
          ))}
        </Card>
      )}

      {/* ══ STEP 3: Review ══ */}
      {step === 3 && (
        <div>
          <Card style={{ marginBottom:12 }}>
            <div style={{ fontSize:12, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:0.8, marginBottom:10 }}>Basic Info</div>
            {[["Title", basic.title], ["Category", basic.category], ["Type", basic.surveyType?.replace(/_/g, " ")], ["Agent", AGENT_META[basic.agent]?.name || basic.agent], ["Modality", basic.modality], ["Multilingual", basic.multilingualEnabled ? "Enabled" : "Disabled"], ["Duration", `${basic.estimatedMins} min`]].map(([label, val]) =>
              val ? <div key={label} style={{ display:"flex", gap:12, marginBottom:4, fontSize:13 }}><span style={{ color:C.muted, minWidth:100 }}>{label}</span><span style={{ color:C.text }}>{val}</span></div> : null
            )}
            {basic.description && <div style={{ marginTop:8, fontSize:12, color:C.muted }}>{basic.description}</div>}
          </Card>
          <Card style={{ marginBottom:12 }}>
            <div style={{ fontSize:12, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:0.8, marginBottom:10 }}>Questions ({questions.length})</div>
            {questions.map((q, i) => (
              <div key={q.id} style={{ borderBottom:`1px solid ${C.border}`, paddingBottom:10, marginBottom:10 }}>
                <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:4 }}>
                  <span style={{ color:C.purple, fontWeight:700, fontSize:12 }}>Q{i + 1}</span>
                  <span style={{ fontSize:11, color:C.muted }}>{q.questionType.replace(/_/g, " ")}</span>
                  {q.isScreener && <span style={{ fontSize:10, background:"#F59E0B22", color:"#F59E0B", padding:"1px 6px", borderRadius:99 }}>Screener</span>}
                </div>
                <div style={{ fontSize:13, color:C.text }}>{q.questionText}</div>
                {(q.skipLogic || []).filter((r) => r.condition).map((r, ri) => (
                  <div key={ri} style={{ fontSize:11, color:C.cyan, fontStyle:"italic", marginTop:3 }}>↳ If "{r.condition}" → {r.skipToIndex === -1 ? "End survey" : `skip to Q${r.skipToIndex + 1}`}</div>
                ))}
              </div>
            ))}
          </Card>
          <Card>
            <div style={{ fontSize:12, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:0.8, marginBottom:10 }}>Targeting</div>
            {[["Sample Size", targeting.targetSampleSize], ["Close Date", targeting.closeDate], ["Incentive", targeting.incentiveType !== "none" ? `${targeting.incentiveType}${targeting.incentiveValue ? ` — ${targeting.incentiveValue}` : ""}` : null]].map(([label, val]) =>
              val ? <div key={label} style={{ display:"flex", gap:12, marginBottom:4, fontSize:13 }}><span style={{ color:C.muted, minWidth:100 }}>{label}</span><span style={{ color:C.text }}>{val}</span></div> : null
            )}
            {[["Age Bands", targeting.targetAgeBands], ["Gender", targeting.targetGenders], ["City Tier", targeting.targetCityTiers], ["Education", targeting.targetEducation], ["Profession", targeting.targetProfessions], ["Industries", targeting.targetIndustries]].map(([label, arr]) =>
              arr?.length ? <div key={label} style={{ marginBottom:6 }}><span style={{ fontSize:11, color:C.muted, textTransform:"uppercase", letterSpacing:0.8 }}>{label}: </span>{arr.map((v) => <span key={v} style={{ fontSize:11, background:C.accentDim, color:C.purple, padding:"2px 8px", borderRadius:99, marginLeft:4 }}>{v}</span>)}</div> : null
            )}
            {!targeting.targetAgeBands?.length && !targeting.targetGenders?.length && <div style={{ fontSize:12, color:C.muted }}>No demographic filters — open to all participants</div>}
            {(targeting.segmentQuotas || []).length > 0 && (
              <div style={{ marginTop:10 }}>
                <span style={{ fontSize:11, color:C.muted, textTransform:"uppercase", letterSpacing:0.8 }}>Segment Quotas: </span>
                {targeting.segmentQuotas.map((q, i) => (
                  <span key={i} style={{ fontSize:11, background:C.accentDim, color:C.purple, padding:"2px 8px", borderRadius:99, marginLeft:4 }}>{q.dimension}: {q.value} — max {q.quota}</span>
                ))}
              </div>
            )}
          </Card>

          {/* ── Approval Banner (for pending_review surveys) ── */}
          {editId && currentStatus === "pending_review" && (
            <Card style={{ marginTop:12, border:`1px solid #F59E0B44`, background:"#F59E0B08" }}>
              <div style={{ fontSize:13, fontWeight:700, color:"#F59E0B", marginBottom:6 }}>Awaiting your approval</div>
              <div style={{ fontSize:12, color:C.muted, marginBottom:12, lineHeight:1.6 }}>
                Review the survey above. Make any edits needed, then approve it so the admin can publish it. Or send it back for revision.
              </div>
              {approvalMsg && <div style={{ fontSize:12, color:approvalMsg.includes("approved") ? "#22C55E" : C.muted, marginBottom:10 }}>{approvalMsg}</div>}
              <div style={{ fontSize:11, color:C.muted, fontStyle:"italic" }}>↓ Approve or send back using the action bar at the bottom of this page.</div>
            </Card>
          )}

          {editId && currentStatus === "approved" && (
            <Card style={{ marginTop:12, border:`1px solid #22C55E44`, background:"#22C55E08" }}>
              <div style={{ fontSize:13, fontWeight:700, color:"#22C55E", marginBottom:4 }}>Survey approved</div>
              <div style={{ fontSize:12, color:C.muted }}>The admin can now publish this survey. You'll be notified once it goes live.</div>
            </Card>
          )}

          {/* ── Approval Trail ── */}
          {editId && (
            <div style={{ marginTop:20 }}>
              <div style={{ fontSize:12, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:0.8, marginBottom:10 }}>Approval Trail</div>
              {trailLoading && <div style={{ fontSize:12, color:C.muted }}>Loading…</div>}
              {!trailLoading && approvalEvents.length === 0 && <div style={{ fontSize:12, color:C.muted }}>No events yet.</div>}
              {approvalEvents.map((ev, i) => {
                const evColor = {sent_for_review:"#8B5CF6",approved:"#22C55E",rejected:"#EF4444",edit:"#F59E0B",published:"#3B82F6"}[ev.event] || C.muted;
                const evLabel = {sent_for_review:"Sent for Review",approved:"Approved",rejected:"Sent Back",edit:"Edited",published:"Published"}[ev.event] || ev.event;
                return (
                  <div key={ev.id || i} style={{ display:"flex", gap:10, marginBottom:12 }}>
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", flexShrink:0 }}>
                      <div style={{ width:8, height:8, borderRadius:"50%", background:evColor, marginTop:4 }}/>
                      {i < approvalEvents.length - 1 && <div style={{ width:1, flex:1, background:C.border, marginTop:3 }}/>}
                    </div>
                    <div style={{ flex:1, paddingBottom:10 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontSize:12, fontWeight:700, color:evColor }}>{evLabel}</span>
                        <span style={{ fontSize:11, color:C.muted }}>by {ev.actorName}</span>
                        <span style={{ fontSize:11, color:C.muted, marginLeft:"auto" }}>{new Date(ev.createdAt).toLocaleString()}</span>
                      </div>
                      {ev.note && <div style={{ fontSize:11, color:C.muted, fontStyle:"italic", marginTop:2 }}>"{ev.note}"</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      </fieldset>

      {/* Send-back reason — appears above the action bar when "Send Back" is chosen */}
      {onLastStep && currentStatus === "pending_review" && showRejectInput && (
        <div style={{ marginTop:20 }}>
          <label style={lStyle}>Reason for sending back (optional)</label>
          <textarea value={rejectNote} onChange={e => setRejectNote(e.target.value)} placeholder="e.g. Please soften the wording on Q3…" rows={2} style={taStyle} />
        </div>
      )}

      {/* Action bar — primary lifecycle actions are the final, right-most controls */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:24, paddingTop:20, borderTop:`1px solid ${C.border}` }}>
        <Btn variant="ghost" onClick={step === 0 ? onBack : () => { setError(""); setStep((s) => s - 1); }}>
          {step === 0 ? "Cancel" : "← Back"}
        </Btn>
        <div style={{ display:"flex", gap:10 }}>
          {!onLastStep ? (
            <Btn onClick={() => { if (step === 0 && !basic.title.trim()) { setError("Title is required."); return; } setError(""); setStep((s) => s + 1); }}>Next →</Btn>
          ) : locked ? (
            <span style={{ fontSize:12, color:C.muted, fontStyle:"italic", alignSelf:"center" }}>🔒 {currentStatus === "approved" ? "Approved — awaiting publish" : currentStatus} · locked for editing</span>
          ) : currentStatus === "pending_review" ? (
            // Review decision is the final action: Save (secondary) · Send Back · Approve (primary)
            showRejectInput ? <>
              <Btn variant="ghost" onClick={() => { setShowRejectInput(false); setRejectNote(""); }}>Cancel</Btn>
              <Btn variant="danger" disabled={rejecting} onClick={handleReject}>{rejecting ? "Sending back…" : "Confirm — Send Back"}</Btn>
            </> : <>
              <Btn variant="ghost" disabled={saving} onClick={() => handleSave("draft")}>{saving ? "Saving…" : "Save Changes"}</Btn>
              <Btn variant="ghost" onClick={() => setShowRejectInput(true)} style={{ color:"#EF4444", borderColor:"#EF4444" }}>✕ Send Back</Btn>
              <Btn disabled={approving} onClick={handleApprove} style={{ background:"linear-gradient(135deg,#22C55E,#16A34A)", color:"#fff", border:"none" }}>{approving ? "Approving…" : "✓ Approve Survey"}</Btn>
            </>
          ) : (
            <Btn variant="ghost" disabled={saving} onClick={() => handleSave("draft")}>{saving ? "Saving…" : "Save Changes"}</Btn>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SURVEY DETAIL PAGE — tabs: Overview / Questions / Ask
// ══════════════════════════════════════════════════════════════════════════════
function SurveyDetailPage({ surveyId, customer, initialTab = "overview", onBack, backLabel = "Back", onEdit }) {
  const { isMobile } = useViewport();
  const [survey, setSurvey]         = useState(null);
  const [tab, setTab]               = useState(initialTab);
  const [loading, setLoading]       = useState(true);
  const [toast, setToast]           = useState("");
  const [error, setError]           = useState("");

  useEffect(() => {
    api("GET", `/surveys/${surveyId}`)
      .then((d) => setSurvey(d.survey))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [surveyId]);

  if (loading) return <div style={{ padding: 40, color: C.muted, textAlign: "center" }}>Loading…</div>;
  if (!survey) return <div style={{ padding: 40, color: C.danger, textAlign: "center" }}>{error || "Survey not found"}</div>;

  const completed = survey.completedSessions || survey.sessions?.filter((s) => s.status === "completed").length || 0;
  const pct = survey.targetSampleSize ? Math.round((completed / survey.targetSampleSize) * 100) : 0;
  const canAskRishi = ["active", "closed"].includes(survey.status);
  const TABS = ["overview", "questions"];
  if (survey.hasAggReport) TABS.push("report");
  if (canAskRishi)         TABS.push("ask");

  return (
    <div style={{ padding: isMobile ? "16px" : "28px 32px", maxWidth: 1100, margin: "0 auto" }}>
      {toast && <Toast message={toast} onDone={() => setToast("")} />}

      <button onClick={onBack} style={{ background: "transparent", border: "none", color: C.muted, cursor: "pointer", fontSize: 13, marginBottom: 16, fontFamily: "inherit" }}>← {backLabel}</button>

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 24 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <h2 style={{ fontSize: 24, fontWeight: 800, color: C.text }}>{survey.title}</h2>
            <StatusBadge status={survey.status} />
          </div>
          <p style={{ fontSize: 13, color: C.muted }}>{survey.category} · {survey.modality} · {survey.agent}</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {survey.status === "pending_review" && (
            <Btn variant="outline" onClick={() => onEdit(survey)}>Review & Edit</Btn>
          )}
          {survey.status === "approved" && (
            <Btn variant="outline" onClick={() => onEdit(survey)}>View</Btn>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <Card style={{ marginBottom: 20, padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontSize: 13, color: C.muted }}>Completion Progress</span>
          <span style={{ fontSize: 13, color: C.text, fontWeight: 700 }}>{completed} / {survey.targetSampleSize || "—"} ({pct}%)</span>
        </div>
        <div style={{ height: 6, background: C.surface, borderRadius: 3, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${pct}%`, background: `linear-gradient(90deg, ${C.purple}, ${C.cyan})`, borderRadius: 3 }} />
        </div>
      </Card>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: `1px solid ${C.border}`, paddingBottom: 0 }}>
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: "9px 18px", background: "transparent", border: "none",
            borderBottom: tab === t ? `2px solid ${C.purple}` : "2px solid transparent",
            color: tab === t ? C.purple : C.muted, cursor: "pointer", fontSize: 13,
            fontWeight: 600, textTransform: "capitalize", fontFamily: "inherit",
          }}>{t === "ask" ? "✨ Ask Rishi" : t}</button>
        ))}
      </div>

      {/* Overview tab */}
      {tab === "overview" && (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
          <Card>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: C.muted, marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.5px" }}>Survey Details</h4>
            {[
              ["Type", survey.surveyType], ["Multilingual", survey.multilingualEnabled ? "Enabled" : "Disabled"], ["Duration", `${survey.estimatedMins} min`],
              ["Sample Size", survey.targetSampleSize],
              ["Incentive", survey.incentiveType ? `${survey.incentiveType} — ₹${survey.incentiveValue}` : "None"],
              ["Close Date", survey.closeDate ? new Date(survey.closeDate).toLocaleDateString("en-IN") : "Open-ended"],
            ].map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${C.border}`, fontSize: 13 }}>
                <span style={{ color: C.muted }}>{k}</span>
                <span style={{ color: C.text, fontWeight: 500 }}>{v || "—"}</span>
              </div>
            ))}
          </Card>
          <Card>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: C.muted, marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.5px" }}>Description</h4>
            <p style={{ fontSize: 13, color: C.text, lineHeight: 1.6, marginBottom: 12 }}>{survey.description}</p>
            {survey.purpose && (
              <>
                <h4 style={{ fontSize: 13, fontWeight: 700, color: C.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>Purpose</h4>
                <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>{survey.purpose}</p>
              </>
            )}
          </Card>
        </div>
      )}

      {/* Questions tab */}
      {tab === "questions" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {(survey.questions || []).map((q, i) => (
            <Card key={i} style={{ padding: 16 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div style={{ minWidth: 28, height: 28, borderRadius: "50%", background: C.accentDim, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: C.purple, flexShrink: 0 }}>{i + 1}</div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 14, color: C.text, marginBottom: q.helpText ? 6 : 0, lineHeight: 1.5 }}>{q.questionText}</p>
                  {q.helpText && <p style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>AI guidance: {q.helpText}</p>}
                  <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background: C.surface, color: C.muted, border: `1px solid ${C.border}` }}>{q.questionType}</span>
                    {q.isScreener && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background: "rgba(245,158,11,0.15)", color: C.gold, border: `1px solid ${C.gold}` }}>Screener</span>}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Report tab — embedded aggregate report (view-only) */}
      {tab === "report" && (
        <AggregateReportPage surveyId={surveyId} embedded />
      )}

      {/* Ask Your Data tab */}
      {tab === "ask" && (
        <AskYourDataPanel surveyId={surveyId} C={C} />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// AGGREGATE REPORT PAGE
// ══════════════════════════════════════════════════════════════════════════════
function AggregateReportPage({ surveyId, onBack, backLabel = "Survey", embedded = false }) {
  const [reportData, setReportData]       = useState(null); // raw API response
  const [content, setContent]             = useState(null); // parsed JSON content
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState("");
  const [toast, setToast]                 = useState("");
  const [feedback, setFeedback]           = useState("");
  const [submitting, setSubmitting]       = useState(false);
  const [hasFeedback, setHasFeedback]     = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [reportWarning, setReportWarning] = useState("");
  const [surveyTitle, setSurveyTitle]     = useState(""); // for the header + a meaningful PDF filename

  // ── Client-side JSON repair (mirrors server-side repairJson + truncation recovery) ──
  const repairJsonClient = (s) => {
    let inStr = false, esc = false, out = "";
    for (let i = 0; i < s.length; i++) {
      const ch = s[i];
      if (esc)         { out += ch; esc = false; continue; }
      if (ch === "\\") { out += ch; esc = true;  continue; }
      if (ch === '"')  { out += ch; inStr = !inStr; continue; }
      if (inStr) {
        if (ch === "\n") { out += "\\n"; continue; }
        if (ch === "\r") { out += "\\r"; continue; }
        if (ch === "\t") { out += "\\t"; continue; }
      }
      out += ch;
    }
    return out;
  };

  const jsonStateClient = (str) => {
    let stack = [], inStr = false, escaped = false;
    for (let i = 0; i < str.length; i++) {
      const ch = str[i];
      if (escaped)     { escaped = false; continue; }
      if (ch === "\\") { escaped = true;  continue; }
      if (ch === '"')  { inStr = !inStr;  continue; }
      if (!inStr) {
        if      (ch === "{" || ch === "[") stack.push(ch === "{" ? "}" : "]");
        else if (ch === "}" || ch === "]") stack.pop();
      }
    }
    return { stack: stack.reverse(), inStr };
  };

  const truncationRecoveryClient = (raw) => {
    const tryClose = (str) => {
      const { stack, inStr } = jsonStateClient(str);
      if (!stack.length && !inStr) return null;
      const suffix = (inStr ? '"' : "") + stack.join("");
      try { const r = JSON.parse(str + suffix); r._truncated = true; return r; } catch { return null; }
    };
    let result = tryClose(raw);
    if (result) return result;
    const safeCommas = [];
    let inStr2 = false, esc2 = false, depth2 = 0;
    for (let i = 0; i < raw.length; i++) {
      const ch = raw[i];
      if (esc2)        { esc2 = false; continue; }
      if (ch === "\\") { esc2 = true;  continue; }
      if (ch === '"')  { inStr2 = !inStr2; continue; }
      if (!inStr2) {
        if (ch === "{" || ch === "[") depth2++;
        else if (ch === "}" || ch === "]") depth2--;
        else if (ch === "," && depth2 > 0) safeCommas.push(i);
      }
    }
    const start2 = Math.max(0, safeCommas.length - 40);
    for (let k = safeCommas.length - 1; k >= start2; k--) {
      result = tryClose(raw.slice(0, safeCommas[k]));
      if (result) return result;
    }
    return null;
  };

  const parseAiJsonClient = (text) => {
    let s = text.replace(/^```(?:json)?\s*/i, "").replace(/\s*```\s*$/, "").trim();
    const complete = s.match(/\{[\s\S]*\}/);
    if (complete) { s = complete[0]; } else { const p = s.match(/\{[\s\S]*/); if (p) s = p[0]; }
    s = repairJsonClient(s);
    try { return JSON.parse(s); } catch {
      const rec = truncationRecoveryClient(s);
      if (rec) return rec;
      throw new Error("JSON parse failed");
    }
  };

  const load = useCallback(() => {
    setLoading(true); setError(""); setReportWarning("");
    // Fetch the survey title (the report content doesn't carry it) so the header
    // and the downloaded PDF filename are meaningful, matching the console app.
    api("GET", `/surveys/${surveyId}`)
      .then((d) => setSurveyTitle(d.survey?.title || ""))
      .catch(() => {});
    api("GET", `/surveys/${surveyId}/aggregate-report`)
      .then((d) => {
        setReportData(d.report);
        let c = d.report?.content;
        if (typeof c === "string") { try { c = parseAiJsonClient(c); } catch { c = null; } }
        if (c?.raw && typeof c.raw === "string" && !c.executiveSummary && !c.keyThemes && !c.questionAnalysis) {
          try { c = { ...parseAiJsonClient(c.raw), _stats: c._stats }; } catch {}
        }
        if (c?._truncated) {
          setReportWarning("This report was generated with abbreviated sections due to data volume. Contact your account manager to request a full regeneration.");
        }
        setContent(c || null);
        setHasFeedback(!!d.report?.hasFeedback);
      })
      .catch((e) => {
        // "being prepared" is expected when no published report exists
        if (e.message?.includes("being prepared")) setContent(null);
        else setError(e.message);
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  useEffect(() => { load(); }, [load]);

  const handleSubmitFeedback = async () => {
    if (!feedback.trim()) return;
    setSubmitting(true);
    try {
      await api("POST", `/surveys/${surveyId}/aggregate-report/feedback`, { comment: feedback.trim() });
      setToast("Feedback submitted! Your account manager will review it.");
      setHasFeedback(true);
      setFeedback("");
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div style={{ padding: 40, color: C.muted, textAlign: "center" }}>Loading report...</div>;

  return (
    <div style={embedded ? { padding: 0 } : { padding: "28px 32px", maxWidth: 1000, margin: "0 auto" }}>
      {toast && <Toast message={toast} onDone={() => setToast("")} />}
      {!embedded && (
        <button onClick={onBack} style={{ background: "transparent", border: "none", color: C.muted, cursor: "pointer", fontSize: 13, marginBottom: 16, fontFamily: "inherit" }}>
          ← {backLabel}
        </button>
      )}

      {error && <div style={{ color: C.danger, padding: 12, background: C.dangerDim, borderRadius: 6, marginBottom: 16 }}>{error}</div>}
      {reportWarning && <div style={{ color: C.warning, padding: 12, background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 6, marginBottom: 16, fontSize: 13 }}>⚠ {reportWarning}</div>}

      {/* No published report */}
      {!content ? (
        <Card style={{ textAlign: "center", padding: 60 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 8 }}>No report generated yet</div>
          <div style={{ fontSize: 13, color: C.muted, maxWidth: 420, margin: "0 auto", lineHeight: 1.7 }}>
            The aggregate report for this survey has not been generated yet. Once it is ready, it will appear here automatically.
            <br /><br />
            Reports are generated by the Rik.ai research team after the survey reaches its target sample size.
          </div>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Header */}
          <div style={{ marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
            <div>
              <h2 style={{ fontSize: 26, fontWeight: 800, color: C.text, marginBottom: 4 }}>
                {surveyTitle || content.surveyTitle || "Aggregate Report"}
              </h2>
              {reportData?.generatedAt && (
                <p style={{ fontSize: 12, color: C.muted }}>
                  v{reportData.version} · Generated {new Date(reportData.generatedAt).toLocaleDateString("en-IN")}
                </p>
              )}
            </div>
            <Btn
              variant="outline"
              disabled={downloadingPdf}
              onClick={async () => {
                setDownloadingPdf(true);
                try {
                  await downloadReportPdf({
                    content,
                    reportMeta: reportData,
                    surveyTitle: surveyTitle || content.surveyTitle,
                    companyName: null,
                  });
                } catch (e) { alert("PDF generation failed: " + e.message); }
                finally { setDownloadingPdf(false); }
              }}
            >
              {downloadingPdf ? "Generating…" : "⬇ Download PDF"}
            </Btn>
          </div>

          {/* Executive Summary */}
          {content.executiveSummary && (
            <Card>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: C.accent, marginBottom: 12 }}>Executive Summary</h3>
              <p style={{ fontSize: 14, color: C.text, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{content.executiveSummary}</p>
            </Card>
          )}

          {/* Methodology Scorecard (Phase 3) — deterministic KPIs; omitted for qual_only */}
          {content.scorecard && content.scorecard.headline ? (() => {
            const sc = content.scorecard, hd = sc.headline;
            const bandColor = (b) => { const u = String(b||"").toUpperCase(); if (["GO","EXCELLENT"].includes(u)) return "#10B981"; if (["REFINE","GOOD"].includes(u)) return "#F59E0B"; if (["KILL","NEEDS ATTENTION"].includes(u)) return C.danger; return C.accent; };
            const fmt = (v,u) => { if (v == null) return "—"; if (u === "nps" || !u) return String(v); if (u === "%" || String(u).startsWith("% ")) return `${v}%`; if (String(u).startsWith("/")) return `${v}${u}`; if (u === "₹") return `₹${v}`; return `${v} ${u}`; };
            // "By segment" cuts are disabled — segment n counts silently exclude
            // supplier-sourced respondents (no demographic profile on file for
            // them), so the base shown would be misleading. See docs/product-backlog.md.
            const segments = [];
            const maxPct = sc.funnel ? Math.max(1, ...sc.funnel.map(f=>f.pct||0)) : 1;
            return (
              <Card>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: C.accent, marginBottom: 12 }}>Methodology Scorecard</h3>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:14, padding:"14px 16px", borderRadius:10, background:C.surface, marginBottom:12 }}>
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:C.accent, textTransform:"uppercase", letterSpacing:"0.6px", marginBottom:4 }}>{hd.label}</div>
                    <div style={{ fontSize:36, fontWeight:900, color:C.accent, lineHeight:1 }}>{hd.value==null?"—":fmt(hd.value,hd.unit)}</div>
                    {hd.interpretation && <div style={{ fontSize:11, color:C.muted, marginTop:6 }}>{hd.interpretation}</div>}
                    {hd.norm && <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>
                      vs benchmark <strong style={{ color:C.text }}>{fmt(hd.norm.value,hd.unit)}</strong>
                      {hd.norm.delta!=null && <span style={{ marginLeft:6, fontWeight:700, color:hd.norm.delta>=0?"#15803D":"#B91C1C" }}>{hd.norm.delta>=0?"▲ +":"▼ "}{hd.norm.delta}</span>}
                      <span style={{ marginLeft:6, opacity:0.7 }}>({hd.norm.sourceType})</span>
                    </div>}
                  </div>
                  {hd.band && <div style={{ fontSize:13, fontWeight:800, color:"#fff", background:bandColor(hd.band), padding:"6px 14px", borderRadius:8, textTransform:"uppercase", letterSpacing:"0.6px" }}>{hd.band}</div>}
                </div>
                {(sc.metrics||[]).length>0 && (
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(120px, 1fr))", gap:10, marginBottom:(segments.length||sc.funnel)?12:0 }}>
                    {sc.metrics.slice(0,6).map((m,i)=>(
                      <div key={i} style={{ textAlign:"center", padding:"10px 8px", background:C.surface, borderRadius:8 }}>
                        <div style={{ fontSize:18, fontWeight:900, color:C.text }}>{fmt(m.value,m.unit)}</div>
                        <div style={{ fontSize:10, color:C.muted, marginTop:2, textTransform:"uppercase", letterSpacing:"0.4px" }}>{m.label}</div>
                      </div>
                    ))}
                  </div>
                )}
                {sc.funnel?.length>0 && (
                  <div style={{ marginBottom:segments.length?12:0 }}>
                    {sc.funnel.map((f,i)=>(
                      <div key={i} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6 }}>
                        <div style={{ fontSize:11, color:C.text, width:150 }}>{f.label}</div>
                        <div style={{ flex:1, height:12, background:C.bg||C.surface, borderRadius:6, overflow:"hidden" }}>
                          <div style={{ height:12, width:`${Math.round(((f.pct||0)/maxPct)*100)}%`, background:C.accent, borderRadius:6 }}/>
                        </div>
                        <div style={{ fontSize:11, fontWeight:700, color:C.accent, width:48, textAlign:"right" }}>{f.pct==null?"—":`${f.pct}%`}</div>
                      </div>
                    ))}
                  </div>
                )}
                {segments.length>0 && (
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:"0.5px", margin:"4px 0 6px" }}>{hd.label} by Segment</div>
                    {segments.map((s,i)=>(
                      <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"5px 0", borderBottom:`1px solid ${C.border}` }}>
                        <div style={{ fontSize:12, color:C.text }}>{s.dimensionLabel}: {s.segment} <span style={{ fontSize:11, color:C.muted }}>(n={s.base})</span></div>
                        {s.suppressed ? <div style={{ fontSize:11, color:C.muted, fontStyle:"italic" }}>insufficient base</div> : <div style={{ fontSize:12, fontWeight:700, color:C.accent }}>{fmt(s.value,s.unit)}</div>}
                      </div>
                    ))}
                  </div>
                )}
                {sc.drivers?.detraction?.length>0 && (
                  <div style={{ marginTop:12 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:6 }}>Drivers of Detraction</div>
                    {sc.drivers.detraction.map((q,i)=>(<div key={i} style={{ fontSize:12, color:C.text, fontStyle:"italic", borderLeft:`2px solid ${C.accent}`, paddingLeft:10, marginBottom:6 }}>"{q}"</div>))}
                  </div>
                )}
              </Card>
            );
          })() : null}

          {/* Executive Action Summary */}
          {content.actionSummary && (content.actionSummary.doNow?.length || content.actionSummary.doNext?.length || content.actionSummary.protect?.length) ? (
            <Card>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: C.accent, marginBottom: 12 }}>Executive Action Summary</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
                {[{ head: "Do Now", color: C.danger, items: content.actionSummary.doNow }, { head: "Do Next", color: C.accent, items: content.actionSummary.doNext }, { head: "Protect", color: "#10B981", items: content.actionSummary.protect }].map((col) => (
                  <div key={col.head} style={{ padding: "10px 12px", background: C.surface, borderRadius: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: col.color, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>{col.head}</div>
                    {(col.items || []).map((it, j) => (<div key={j} style={{ fontSize: 12, color: C.text, lineHeight: 1.45, marginBottom: 4 }}>• {it}</div>))}
                  </div>
                ))}
              </div>
            </Card>
          ) : null}

          {/* Engagement Metrics */}
          {content.engagementMetrics && (
            <Card>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: C.accent, marginBottom: 12 }}>Engagement Metrics</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 10 }}>
                {[
                  { label: "Avg WPM", val: content.engagementMetrics.avgWpm },
                  { label: "Avg Sentiment", val: content.engagementMetrics.avgSentimentScore != null ? Number(content.engagementMetrics.avgSentimentScore).toFixed(2) : null },
                  { label: "Completion", val: content.engagementMetrics.completionRate },
                ].filter(m => m.val != null).map((m) => (
                  <div key={m.label} style={{ textAlign: "center", padding: "12px 8px", background: C.surface, borderRadius: 8 }}>
                    <div style={{ fontSize: 20, fontWeight: 900, color: C.text }}>{m.val}</div>
                    <div style={{ fontSize: 10, color: C.muted, marginTop: 2, textTransform: "uppercase", letterSpacing: "0.5px" }}>{m.label}</div>
                  </div>
                ))}
              </div>
              {content.engagementMetrics.narrative && (
                <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>{content.engagementMetrics.narrative}</p>
              )}
            </Card>
          )}

          {/* Key Themes */}
          {content.keyThemes?.length > 0 && (
            <Card>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: C.accent, marginBottom: 12 }}>Key Themes</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {content.keyThemes.map((t, i) => (
                  <div key={i} style={{ padding: "12px 14px", background: C.surface, borderRadius: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{t.theme}</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {t.confidence && <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", padding: "2px 7px", borderRadius: 20, border: `1px solid ${t.confidence === "high" ? "#10B981" : t.confidence === "low" ? C.danger : "#F59E0B"}`, color: t.confidence === "high" ? "#10B981" : t.confidence === "low" ? C.danger : "#F59E0B" }}>{t.confidence}</span>}
                        <span style={{ fontSize: 11, color: t.sentiment === "positive" ? C.cyan : t.sentiment === "negative" ? C.danger : C.muted }}>{t.frequency}</span>
                      </div>
                    </div>
                    <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{t.description}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Drivers & Barriers */}
          {content.driversAndBarriers && (content.driversAndBarriers.drivers?.length || content.driversAndBarriers.barriers?.length) ? (
            <Card>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: C.accent, marginBottom: 12 }}>Drivers &amp; Barriers</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
                {[{ head: "Drivers", color: "#10B981", items: content.driversAndBarriers.drivers }, { head: "Barriers", color: C.danger, items: content.driversAndBarriers.barriers }].map((col) => (
                  <div key={col.head} style={{ padding: "10px 12px", background: C.surface, borderRadius: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: col.color, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>{col.head}</div>
                    {(col.items || []).map((it, j) => (<div key={j} style={{ fontSize: 12, color: C.text, lineHeight: 1.45, marginBottom: 4 }}>• {it}</div>))}
                  </div>
                ))}
              </div>
            </Card>
          ) : null}

          {/* Question Analysis */}
          {content.questionAnalysis?.length > 0 && content.questionAnalysis.map((qa, i) => (
            <Card key={i}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: C.blue, marginBottom: 6 }}>Q{qa.questionIndex + 1}: {qa.questionText}</h3>
              {qa.quantitativeStat && <div style={{ fontSize: 12, color: C.accent, fontWeight: 600, marginBottom: 6 }}>{qa.quantitativeStat}</div>}
              <p style={{ fontSize: 13, color: C.text, lineHeight: 1.6, marginBottom: 10 }}>{qa.summary}</p>

              {qa.sentimentBreakdown && (
                <div style={{ display: "flex", gap: 16, marginBottom: 10 }}>
                  {Object.entries(qa.sentimentBreakdown).map(([k, v]) => (
                    <span key={k} style={{ fontSize: 12, color: k === "positive" ? C.cyan : k === "negative" ? C.danger : C.muted }}>
                      {k}: <strong>{v}</strong>
                    </span>
                  ))}
                </div>
              )}

              {qa.notableQuotes?.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Notable Quotes</div>
                  {qa.notableQuotes.map((q, j) => (
                    <div key={j} style={{ padding: "8px 12px", borderLeft: `3px solid ${C.accent}`, background: C.surface, borderRadius: "0 6px 6px 0", fontSize: 12, color: C.text, fontStyle: "italic", lineHeight: 1.5, marginBottom: 4 }}>
                      "{q}"
                    </div>
                  ))}
                </div>
              )}

              {qa.insights && <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}><strong>Insight:</strong> {qa.insights}</p>}
            </Card>
          ))}

          {/* Verbatim Highlights */}
          {content.verbatimHighlights?.length > 0 && (
            <Card>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: C.accent, marginBottom: 12 }}>Verbatim Highlights</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {content.verbatimHighlights.map((vh, i) => (
                  <div key={i} style={{
                    padding: "10px 14px",
                    borderLeft: `3px solid ${vh.sentiment === "positive" ? C.cyan : vh.sentiment === "negative" ? C.danger : C.accent}`,
                    background: C.surface, borderRadius: "0 6px 6px 0",
                  }}>
                    <div style={{ fontSize: 13, color: C.text, fontStyle: "italic", lineHeight: 1.5, marginBottom: 4 }}>"{vh.quote}"</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{vh.respondent} · {vh.question}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Sentiment Analysis */}
          {content.sentimentAnalysis && (
            <Card>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: C.accent, marginBottom: 10 }}>Sentiment Analysis</h3>
              <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: C.text }}>
                  Overall: <strong style={{ color: content.sentimentAnalysis.overall === "positive" ? C.cyan : content.sentimentAnalysis.overall === "negative" ? C.danger : C.muted }}>
                    {content.sentimentAnalysis.overall}
                  </strong>
                </span>
              </div>
              {content.sentimentAnalysis.narrative && (
                <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>{content.sentimentAnalysis.narrative}</p>
              )}
            </Card>
          )}

          {/* Demographic Insights */}
          {content.demographicInsights && (
            <Card>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: C.accent, marginBottom: 10 }}>Demographic Insights</h3>
              <p style={{ fontSize: 13, color: C.text, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{content.demographicInsights}</p>
            </Card>
          )}

          {/* Recommendations */}
          {content.recommendations?.length > 0 && (
            <Card>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: C.gold, marginBottom: 12 }}>Recommendations</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {content.recommendations.map((r, i) => (
                  <div key={i} style={{
                    padding: "12px 14px", background: C.surface, borderRadius: 8,
                    borderLeft: `3px solid ${r.priority === "high" ? C.danger : r.priority === "medium" ? C.gold : C.cyan}`,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{r.title}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, textTransform: "uppercase",
                        background: r.priority === "high" ? C.dangerDim : r.priority === "medium" ? "rgba(245,158,11,0.12)" : "rgba(20,184,166,0.12)",
                        color: r.priority === "high" ? C.danger : r.priority === "medium" ? C.gold : C.cyan,
                      }}>{r.priority}</span>
                    </div>
                    <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, marginBottom: 0 }}>{r.description}</p>
                    <div style={{ display: "flex", gap: 14, marginTop: 6, flexWrap: "wrap" }}>
                      {r.impact && <span style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>Impact: {r.impact}</span>}
                      {r.effort && <span style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>Effort: {r.effort}</span>}
                      {r.owner && <span style={{ fontSize: 10, color: C.accent, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>Owner: {r.owner}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Conclusion */}
          {content.conclusion && (
            <Card>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: C.accent, marginBottom: 10 }}>Conclusion</h3>
              <p style={{ fontSize: 13, color: C.text, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{content.conclusion}</p>
            </Card>
          )}

          {/* Methodology */}
          {content.methodologyNote && (
            <Card style={{ background: C.surface }}>
              <h3 style={{ fontSize: 12, fontWeight: 700, color: C.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>Methodology</h3>
              <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{content.methodologyNote}</p>
            </Card>
          )}

          {/* ── Generic fallback: any non-standard keys Claude returned ── */}
          {(() => {
            const KNOWN = new Set(["executiveSummary","actionSummary","scorecard","respondentProfile","engagementMetrics","keyThemes","driversAndBarriers","questionAnalysis","verbatimHighlights","sentimentAnalysis","demographicInsights","recommendations","conclusion","methodologyNote","_stats","surveyTitle","completedAt","totalParticipants","overallSentiment","questionInsights","strategicRecommendations"]);
            const camelToTitle = k => k.replace(/([A-Z])/g, " $1").replace(/^./, c => c.toUpperCase()).trim();
            const renderGenericValue = (val) => {
              if (val === null || val === undefined) return null;
              if (typeof val === "string" || typeof val === "number") {
                return <p style={{ fontSize: 13, color: C.text, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{String(val)}</p>;
              }
              if (Array.isArray(val)) {
                if (val.length === 0) return null;
                if (typeof val[0] === "string") {
                  return <ul style={{ margin: 0, padding: "0 0 0 16px" }}>{val.map((s, i) => <li key={i} style={{ fontSize: 13, color: C.text, lineHeight: 1.7, marginBottom: 4 }}>{s}</li>)}</ul>;
                }
                return val.map((item, i) => (
                  <div key={i} style={{ padding: "10px 12px", background: C.surface, borderRadius: 6, marginBottom: 8, fontSize: 12, color: C.text }}>
                    {Object.entries(item).map(([k, v]) => (
                      <div key={k} style={{ marginBottom: 4 }}><span style={{ color: C.muted, marginRight: 6 }}>{camelToTitle(k)}:</span>{typeof v === "object" ? JSON.stringify(v) : String(v ?? "")}</div>
                    ))}
                  </div>
                ));
              }
              if (typeof val === "object") {
                return Object.entries(val).map(([k, v]) => (
                  <div key={k} style={{ marginBottom: 6 }}>
                    <span style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 2 }}>{camelToTitle(k)}</span>
                    <span style={{ fontSize: 13, color: C.text }}>{typeof v === "object" ? JSON.stringify(v) : String(v ?? "")}</span>
                  </div>
                ));
              }
              return <p style={{ fontSize: 13, color: C.text }}>{String(val)}</p>;
            };
            const isEmptyVal = (v) => {
              if (v === null || v === undefined) return true;
              if (typeof v === "string") return v.trim() === "";
              if (Array.isArray(v)) return v.length === 0;
              if (typeof v === "object") return Object.keys(v).length === 0;
              return false;
            };
            const customEntries = Object.entries(content).filter(([k, v]) => !KNOWN.has(k) && !isEmptyVal(v));
            if (customEntries.length === 0) return null;
            return customEntries.map(([key, val]) => (
              <Card key={key}>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: C.accent, marginBottom: 10 }}>{camelToTitle(key)}</h3>
                {renderGenericValue(val)}
              </Card>
            ));
          })()}

          {/* Feedback section */}
          <Card>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: C.accent, marginBottom: 8 }}>Report Feedback</h3>
            {hasFeedback ? (
              <div style={{ padding: "14px 16px", background: "rgba(20,184,166,0.08)", border: `1px solid rgba(20,184,166,0.2)`, borderRadius: 8 }}>
                <p style={{ fontSize: 13, color: C.cyan, fontWeight: 600, marginBottom: 4 }}>Feedback submitted</p>
                <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
                  Your feedback has been sent to the research team. A revised report will be published once your requested changes are incorporated.
                </p>
              </div>
            ) : (
              <>
                <p style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>
                  If you'd like any changes to this report — different focus areas, additional analysis, formatting requests — describe them below.
                </p>
                <Textarea
                  value={feedback} onChange={setFeedback} rows={4}
                  placeholder="e.g. Could you add more detail on the 25-34 age segment? We'd also like a section comparing results with our Q1 study."
                />
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                  <Btn onClick={handleSubmitFeedback} disabled={submitting || !feedback.trim()}>
                    {submitting ? "Submitting..." : "Submit Feedback"}
                  </Btn>
                </div>
              </>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PROJECT WORKSPACE PAGE — 4-tab per-project view
// ══════════════════════════════════════════════════════════════════════════════
function ProjectWorkspacePage({ projectId, customerId, initialTab = "overview", onBack, backLabel = "Back", onViewSurvey }) {
  const { isMobile, isTablet } = useViewport();
  const [project, setProject]           = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState("");
  const [activeTab, setActiveTab]       = useState(initialTab);
  const [toast, setToast]               = useState("");
  const [expandedQ, setExpandedQ]       = useState({});     // { [surveyId_qIdx]: true }
  const [askSurveyId, setAskSurveyId]   = useState(null);   // which survey the AYD panel is open for

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api("GET", `/customers/${customerId}/projects/${projectId}`)
      .then(data => { if (!cancelled) { setProject(data); setLoading(false); } })
      .catch(err  => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [customerId, projectId]);

  const pad     = isMobile ? "16px" : isTablet ? "20px 24px" : "28px 32px";
  const TABS    = ["overview", "surveys", "financials"];
  const TAB_LBL = { overview: "Overview", surveys: "Surveys", financials: "Financials" };

  // ── helpers ──
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : null;
  const fmtMon  = (d) => d ? new Date(d).toLocaleDateString("en-IN", { month: "short", year: "numeric" }) : null;

  const statusColor = (s) => ({
    active: C.accent, completed: C.success, on_hold: C.warning, cancelled: C.danger,
    draft: C.muted, sent: C.cyan, paid: C.success, overdue: C.danger, pending: C.muted,
  }[s] || C.muted);

  const ragColor = (r) => r === "red" ? C.danger : r === "amber" ? C.warning : r === "green" ? C.success : C.border;

  // ── loading / error ──
  if (loading) {
    return (
      <div style={{ padding: pad, maxWidth: 1200, margin: "0 auto" }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, padding: 0, fontFamily: "inherit", marginBottom: 20 }}>
          ← {backLabel}
        </button>
        <div style={{ color: C.muted, fontSize: 14, padding: "60px 0", textAlign: "center" }}>Loading project…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: pad, maxWidth: 1200, margin: "0 auto" }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, padding: 0, fontFamily: "inherit", marginBottom: 20 }}>
          ← {backLabel}
        </button>
        <div style={{ color: C.danger, fontSize: 14 }}>{error}</div>
      </div>
    );
  }

  const surveys   = project?.surveys   || [];
  const documents = project?.documents || [];
  const invoices  = project?.invoices  || [];
  const fin       = project?.financials || {};

  // Timeline progress
  const timelineProgress = (() => {
    if (!project?.startDate || !project?.endDate) return null;
    const start = new Date(project.startDate).getTime();
    const end   = new Date(project.endDate).getTime();
    const now   = Date.now();
    if (end <= start) return null;
    return Math.min(100, Math.max(0, Math.round(((now - start) / (end - start)) * 100)));
  })();

  return (
    <div style={{ padding: pad, maxWidth: 1200, margin: "0 auto" }}>
      {toast && <Toast message={toast} onDone={() => setToast("")} />}

      {/* ── Back + Project header ── */}
      <div style={{ marginBottom: 20 }}>
        <button
          onClick={onBack}
          style={{ background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, padding: 0, fontFamily: "inherit", marginBottom: 12, minHeight: 36 }}
        >
          ← {backLabel}
        </button>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ fontSize: isMobile ? 20 : 26, fontWeight: 800, color: C.text, margin: 0, marginBottom: 6 }}>
              {project.name}
            </h2>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{
                fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 12,
                background: `${statusColor(project.status)}22`,
                color: statusColor(project.status), textTransform: "capitalize",
              }}>{project.status?.replace(/_/g, " ")}</span>
              {project.startDate && (
                <span style={{ fontSize: 11, color: C.muted }}>
                  {fmtMon(project.startDate)} → {project.endDate ? fmtMon(project.endDate) : "Ongoing"}
                </span>
              )}
              <span style={{ fontSize: 11, color: C.muted }}>{surveys.length} survey{surveys.length !== 1 ? "s" : ""}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tab bar — horizontally scrollable on mobile ── */}
      <div style={{
        display: "flex", gap: 4, marginBottom: 24,
        overflowX: "auto", WebkitOverflowScrolling: "touch",
        scrollbarWidth: "none", msOverflowStyle: "none",
        borderBottom: `1px solid ${C.border}`, paddingBottom: 0,
      }}>
        {TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              flexShrink: 0, padding: isMobile ? "10px 14px" : "11px 20px",
              fontSize: isMobile ? 12 : 13, fontWeight: 600, cursor: "pointer",
              background: "none", border: "none", fontFamily: "inherit",
              color: activeTab === tab ? C.accent : C.muted,
              borderBottom: `2px solid ${activeTab === tab ? C.accent : "transparent"}`,
              transition: "all 0.15s", minHeight: 44,
            }}
          >{TAB_LBL[tab]}</button>
        ))}
      </div>

      {/* ══════════ TAB: OVERVIEW ══════════ */}
      {activeTab === "overview" && (
        <div style={{ display: "flex", flexDirection: (isMobile || isTablet) ? "column" : "row", gap: 16, alignItems: (isMobile || isTablet) ? "stretch" : "flex-start" }}>

          {/* Left column — description + timeline */}
          <div style={{ flex: 1, minWidth: 0, width: "100%", display: "flex", flexDirection: "column", gap: 14 }}>
            {project.description && (
              <Card style={{ padding: 18 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>About</div>
                <p style={{ fontSize: 13, color: C.textDim, lineHeight: 1.7, margin: 0 }}>{project.description}</p>
              </Card>
            )}

            {/* Timeline */}
            {(project.startDate || project.endDate) && (
              <Card style={{ padding: 18 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 12 }}>Timeline</div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: C.textDim, marginBottom: 8 }}>
                  <span>{fmtDate(project.startDate) || "–"}</span>
                  <span>{fmtDate(project.endDate) || "Ongoing"}</span>
                </div>
                {timelineProgress !== null && (
                  <>
                    <div style={{ height: 6, background: C.surface, borderRadius: 3, overflow: "hidden", marginBottom: 6 }}>
                      <div style={{
                        height: "100%", width: `${timelineProgress}%`,
                        background: `linear-gradient(90deg, ${C.accent}, ${C.cyan})`,
                        borderRadius: 3, transition: "width 0.6s",
                      }} />
                    </div>
                    <div style={{ fontSize: 11, color: C.muted, textAlign: "right" }}>{timelineProgress}% elapsed</div>
                  </>
                )}
              </Card>
            )}

            {/* Documents */}
            {documents.length > 0 && (
              <Card style={{ padding: 18 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 12 }}>Documents</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {documents.map(doc => (
                    <div key={doc.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 6,
                        background: `${C.cyan}22`, color: C.cyan, textTransform: "uppercase", flexShrink: 0,
                      }}>{doc.type?.toUpperCase()}</span>
                      <span style={{ fontSize: 13, color: C.text, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {doc.name}
                      </span>
                      <span style={{
                        fontSize: 10, padding: "2px 7px", borderRadius: 6,
                        background: `${statusColor(doc.status)}22`, color: statusColor(doc.status),
                        textTransform: "capitalize", flexShrink: 0,
                      }}>{doc.status}</span>
                      {doc.fileUrl && (
                        <a href={doc.fileUrl} target="_blank" rel="noreferrer"
                          style={{ fontSize: 11, color: C.accent, textDecoration: "none", flexShrink: 0 }}>
                          ↗ View
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* ══════════ TAB: SURVEYS ══════════ */}
      {activeTab === "surveys" && (
        <div>
          {surveys.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: C.muted }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
              <div style={{ fontSize: 14 }}>No surveys linked to this project yet.</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {surveys.map(s => {
                const rc = ragColor(s.ragStatus);
                const awaitingReport = !s.hasAggReport && (s.reportReadiness === "ready" || s.reportReadiness === "early_insights");
                const isAskOpen = askSurveyId === s.id;
                const canAskRishi = ["active", "closed"].includes(s.status);
                return (
                  <div key={s.id} style={{ borderRadius: 12, border: `1px solid ${C.border}`, borderLeft: `3px solid ${rc}`, overflow: "hidden", transition: "all 0.15s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.borderLeftColor = rc; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.borderLeftColor = rc; }}
                  >
                    {/* Main card row — clickable */}
                    <div onClick={() => onViewSurvey(s.id)} style={{ background: C.card, padding: isMobile ? "14px 14px" : "16px 20px", cursor: "pointer" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5, flexWrap: "wrap" }}>
                            <RagDot status={s.ragStatus} />
                            <span style={{ fontSize: isMobile ? 13 : 15, fontWeight: 700, color: C.text }}>{s.title}</span>
                            <StatusBadge status={s.status} />
                            <ReadinessBadge readiness={s.reportReadiness} />
                          </div>
                          <div style={{ display: "flex", gap: 10, fontSize: 11, color: C.muted, flexWrap: "wrap" }}>
                            <span style={{ color: rc }}>{ragLabel(s.ragReason)}</span>
                            {s.daysRemaining != null && s.status === "active" && <span>{s.daysRemaining}d left</span>}
                            {s.responsesToday > 0 && <span style={{ color: C.success }}>+{s.responsesToday} today</span>}
                            <span>{s.completedCount}/{s.targetSampleSize || "?"} responses</span>
                            {s.category && <span style={{ color: C.cyan }}>{s.category}</span>}
                          </div>
                        </div>
                        <div style={{ textAlign: "center", flexShrink: 0 }}>
                          <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 900, color: C.accent }}>{s.completionPercent}%</div>
                          <div style={{ fontSize: 9, color: C.muted }}>Done</div>
                        </div>
                      </div>
                      <div style={{ marginTop: 10, height: 3, background: C.surface, borderRadius: 2, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${s.completionPercent}%`, background: rc === C.border ? `linear-gradient(90deg, ${C.accent}, ${C.cyan})` : rc, borderRadius: 2, transition: "width 0.6s" }} />
                      </div>
                    </div>

                    {/* Report status row — customers view reports (the research team generates them) */}
                    {(s.hasAggReport || awaitingReport || canAskRishi) && (
                      <div onClick={e => e.stopPropagation()} style={{ background: C.surface, borderTop: `1px solid ${C.border}`, padding: "10px 20px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        {s.hasAggReport ? (
                          <span style={{ fontSize: 11, color: C.success, flex: 1 }}>
                            ● Report ready{s.aggReportGeneratedAt ? ` · generated ${fmtDate(s.aggReportGeneratedAt)}` : ""}
                          </span>
                        ) : awaitingReport ? (
                          <span style={{ fontSize: 11, color: C.muted, flex: 1 }}>Target reached — your report is being prepared</span>
                        ) : canAskRishi ? (
                          <span style={{ fontSize: 11, color: C.muted, flex: 1 }}>Responses coming in — ask Rishi anything</span>
                        ) : null}
                        {s.hasAggReport && (
                          <Btn variant="primary" onClick={() => onViewSurvey(s.id, "report")} style={{ fontSize: 11, padding: "5px 12px" }}>
                            View Report
                          </Btn>
                        )}
                        {canAskRishi && (
                          <Btn variant="outline" onClick={() => setAskSurveyId(isAskOpen ? null : s.id)} style={{ fontSize: 11, padding: "5px 12px" }}>
                            {isAskOpen ? "✕ Close Rishi" : "✨ Ask Rishi"}
                          </Btn>
                        )}
                      </div>
                    )}

                    {/* Inline Ask Rishi panel */}
                    {isAskOpen && (
                      <div style={{ background: C.surface, borderTop: `1px solid ${C.border}`, padding: 16 }}>
                        <AskYourDataPanel surveyId={s.id} customerId={customerId} C={C} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══════════ TAB: FINANCIALS ══════════ */}
      {activeTab === "financials" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Summary strip */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 10 }}>
            {[
              { label: "Total Billed", value: fin.totalBilled,  color: C.text },
              { label: "Paid",         value: fin.totalPaid,    color: C.success },
              { label: "Overdue",      value: fin.totalOverdue, color: fin.totalOverdue > 0 ? C.danger : C.muted },
              { label: "Pending",      value: fin.totalPending, color: C.gold },
            ].map(s => (
              <Card key={s.label} style={{ textAlign: "center", padding: isMobile ? "12px 8px" : "14px 10px" }}>
                <div style={{ fontSize: isMobile ? 16 : 20, fontWeight: 900, color: s.color }}>
                  ₹{(s.value || 0).toLocaleString("en-IN")}
                </div>
                <div style={{ fontSize: 10, color: C.muted, marginTop: 2, textTransform: "uppercase", letterSpacing: "0.5px" }}>{s.label}</div>
              </Card>
            ))}
          </div>

          {/* Invoice list */}
          <Card style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Invoices</div>
            </div>
            {invoices.length === 0 ? (
              <div style={{ padding: 28, textAlign: "center", color: C.muted, fontSize: 13 }}>No invoices yet.</div>
            ) : (
              <div>
                {/* Table header — hidden on mobile */}
                {!isMobile && (
                  <div style={{
                    display: "grid", gridTemplateColumns: "1.8fr 2fr 1fr 1fr 1fr",
                    padding: "8px 18px", borderBottom: `1px solid ${C.border}`,
                    fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px",
                  }}>
                    <span>Invoice</span><span>Description</span><span>Amount</span><span>Due</span><span>Status</span>
                  </div>
                )}
                {invoices.map((inv, i) => (
                  <div
                    key={inv.id}
                    style={{
                      display: isMobile ? "block" : "grid",
                      gridTemplateColumns: "1.8fr 2fr 1fr 1fr 1fr",
                      padding: isMobile ? "14px 18px" : "12px 18px",
                      borderBottom: i < invoices.length - 1 ? `1px solid ${C.border}` : "none",
                      gap: 8, alignItems: "center",
                    }}
                  >
                    {isMobile ? (
                      <>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{inv.invoiceNum}</span>
                          <span style={{
                            fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 8,
                            background: `${statusColor(inv.status)}22`, color: statusColor(inv.status), textTransform: "capitalize",
                          }}>{inv.status}</span>
                        </div>
                        <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>{inv.description || "–"}</div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                          <span style={{ fontWeight: 700, color: C.text }}>₹{(inv.total || 0).toLocaleString("en-IN")}</span>
                          <span style={{ color: C.muted }}>{fmtDate(inv.dueAt) || "–"}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{inv.invoiceNum}</span>
                        <span style={{ fontSize: 12, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inv.description || "–"}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>₹{(inv.total || 0).toLocaleString("en-IN")}</span>
                        <span style={{ fontSize: 12, color: C.muted }}>{fmtDate(inv.dueAt) || "–"}</span>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 8,
                          background: `${statusColor(inv.status)}22`, color: statusColor(inv.status),
                          textTransform: "capitalize", display: "inline-block",
                        }}>{inv.status}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SUPPORT PAGE — customer ticketing (issues + feedback)
// ══════════════════════════════════════════════════════════════════════════════

const ISSUE_CATEGORIES = [
  { value: "project",  label: "Project" },
  { value: "survey",   label: "Survey" },
  { value: "portal",   label: "Portal / UI" },
  { value: "billing",  label: "Billing & Invoices" },
  { value: "other",    label: "Other" },
];
const FEEDBACK_CATEGORIES = [
  { value: "portal_ux",        label: "Portal Experience" },
  { value: "survey_quality",   label: "Survey Quality" },
  { value: "feature_request",  label: "Feature Request" },
  { value: "pricing",          label: "Pricing & Plans" },
  { value: "other",            label: "Other" },
];
const PRIORITIES = [
  { value: "low",    label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high",   label: "High" },
  { value: "urgent", label: "Urgent" },
];

function TicketStatusBadge({ status }) {
  const map = {
    open:      { bg: "rgba(245,158,11,0.15)",  color: "#F59E0B", label: "Open" },
    in_review: { bg: "rgba(79,110,247,0.15)",  color: "#4F6EF7", label: "In Review" },
    resolved:  { bg: "rgba(16,185,129,0.15)",  color: "#10B981", label: "Resolved" },
    closed:    { bg: "rgba(100,116,139,0.15)", color: "#64748B", label: "Closed" },
  };
  const s = map[status] || map.open;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
      background: s.bg, color: s.color, textTransform: "uppercase", letterSpacing: "0.5px",
    }}>{s.label}</span>
  );
}

function TypeChip({ type }) {
  const isIssue = type === "issue";
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
      background: isIssue ? "rgba(239,68,68,0.12)" : "rgba(79,110,247,0.12)",
      color: isIssue ? C.danger : C.accent, textTransform: "uppercase", letterSpacing: "0.5px",
    }}>{isIssue ? "Issue" : "Feedback"}</span>
  );
}

const ALLOWED_ATTACH_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp", "application/pdf"];
const MAX_ATTACH_SIZE = 5 * 1024 * 1024;

function FileIcon({ mimeType }) {
  if (mimeType && mimeType.startsWith("image/")) {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <rect x="2" y="2" width="20" height="20" rx="3" fill="rgba(59,130,246,0.15)" stroke="#3B82F6" strokeWidth="1.5"/>
        <circle cx="8" cy="9" r="1.8" fill="#3B82F6"/>
        <path d="M2 18l5.5-5.5 3.5 3.5 3-3 8 8" stroke="#3B82F6" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round"/>
      </svg>
    );
  }
  if (mimeType === "application/pdf") {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <rect x="2" y="2" width="20" height="20" rx="3" fill="rgba(239,68,68,0.15)" stroke="#EF4444" strokeWidth="1.5"/>
        <text x="12" y="15.5" textAnchor="middle" fontSize="7.5" fontWeight="800" fill="#EF4444" fontFamily="system-ui,sans-serif">PDF</text>
      </svg>
    );
  }
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <rect x="2" y="2" width="20" height="20" rx="3" fill="rgba(100,116,139,0.15)" stroke="#64748B" strokeWidth="1.5"/>
      <path d="M7 9h10M7 13h7" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function AttachmentList({ attachments, ticketId, onDelete }) {
  const fmtSize = (b) => b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / (1024 * 1024)).toFixed(1)} MB`;
  const baseUrl = `/api/customer-portal/tickets/${ticketId}/attachments`;
  const truncate = (s, n) => s.length > n ? s.slice(0, n - 1) + "…" : s;

  if (!attachments || attachments.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
      {attachments.map(att => (
        <div key={att.id} style={{ position: "relative" }}>
          <a
            href={`${baseUrl}/${att.id}`}
            target="_blank" rel="noreferrer"
            style={{ display: "flex", alignItems: "center", gap: 8, background: "#0e1117", border: "1px solid #273449", borderRadius: 8, padding: "6px 10px", textDecoration: "none", maxWidth: 200 }}
          >
            <FileIcon mimeType={att.mimeType} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, color: "#CBD5E1", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{truncate(att.originalName, 22)}</div>
              <div style={{ fontSize: 10, color: "#64748B" }}>{fmtSize(att.sizeBytes)}</div>
            </div>
          </a>
          {onDelete && att.uploadedByAdminEmail === null && (
            <button onClick={() => onDelete(att.id)} style={{ position: "absolute", top: -4, right: -4, background: "#EF4444", border: "none", borderRadius: "50%", color: "#fff", fontSize: 10, cursor: "pointer", width: 16, height: 16, lineHeight: "16px", textAlign: "center", padding: 0 }}>×</button>
          )}
        </div>
      ))}
    </div>
  );
}

function FileUploadArea({ files, onChange }) {
  const inputRef = useRef(null);
  const fmtSize = (b) => b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / (1024 * 1024)).toFixed(1)} MB`;
  const [validationError, setValidationError] = useState("");

  function addFiles(incoming) {
    setValidationError("");
    const rejected = [];
    const valid = Array.from(incoming).filter(f => {
      if (!ALLOWED_ATTACH_TYPES.includes(f.type)) { rejected.push(`"${f.name}" — unsupported type`); return false; }
      if (f.size > MAX_ATTACH_SIZE) { rejected.push(`"${f.name}" — exceeds 5 MB`); return false; }
      return true;
    });
    if (rejected.length) setValidationError(rejected.join(", "));
    onChange([...files, ...valid].slice(0, 5));
  }

  function onDrop(e) {
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  }

  return (
    <div>
      <label style={{ fontSize: 13, color: "#8894A8", fontWeight: 500, display: "block", marginBottom: 6 }}>Attachments <span style={{ fontSize: 11, color: "#64748B" }}>(optional · max 5 files · 5 MB each · JPEG, PNG, GIF, WebP, PDF)</span></label>
      <div
        onDrop={onDrop} onDragOver={e => e.preventDefault()}
        onClick={() => inputRef.current?.click()}
        style={{ border: `1.5px dashed #273449`, borderRadius: 8, padding: "14px 16px", textAlign: "center", cursor: "pointer", fontSize: 12, color: "#64748B", background: "#0e1117" }}
      >
        {files.length === 0 ? "Click or drag files here" : `${files.length} file${files.length > 1 ? "s" : ""} selected`}
        <input ref={inputRef} type="file" multiple accept={ALLOWED_ATTACH_TYPES.join(",")} style={{ display: "none" }} onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
      </div>
      {validationError && (
        <div style={{ marginTop: 6, fontSize: 12, color: "#EF4444" }}>Rejected: {validationError}. Only JPEG, PNG, GIF, WebP, PDF allowed (max 5 MB each).</div>
      )}
      {files.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          {files.map((f, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, background: "#111826", border: "1px solid #273449", borderRadius: 6, padding: "4px 8px", fontSize: 11 }}>
              <span style={{ color: "#F1F5F9", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
              <span style={{ color: "#64748B" }}>{fmtSize(f.size)}</span>
              <button onClick={e => { e.stopPropagation(); onChange(files.filter((_, j) => j !== i)); }} style={{ background: "none", border: "none", color: "#EF4444", cursor: "pointer", fontSize: 13, padding: 0, lineHeight: 1 }}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NewTicketModal({ customer, onClose, onCreated }) {
  const { isMobile } = useViewport();
  const [type, setType]           = useState("issue");
  const [category, setCategory]   = useState("portal");
  const [priority, setPriority]   = useState("normal");
  const [subject, setSubject]     = useState("");
  const [description, setDescription] = useState("");
  const [surveyId, setSurveyId]   = useState("");
  const [surveys, setSurveys]     = useState([]);
  const [files, setFiles]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");

  useEffect(() => {
    api("GET", `/customers/${customer.customerId}/dashboard`)
      .then(d => setSurveys((d.surveys || []).map(s => ({ value: s.id, label: s.title }))))
      .catch(() => {});
  }, [customer.customerId]);

  // Reset category when type changes
  useEffect(() => {
    setCategory(type === "issue" ? "portal" : "portal_ux");
  }, [type]);

  const cats = type === "issue" ? ISSUE_CATEGORIES : FEEDBACK_CATEGORIES;

  async function submit(e) {
    e.preventDefault();
    if (!subject.trim()) return setError("Please enter a subject.");
    if (!description.trim()) return setError("Please describe the issue or feedback.");
    setError(""); setLoading(true);
    try {
      const body = { type, category, subject: subject.trim(), description: description.trim(), priority };
      if (surveyId) body.surveyId = surveyId;
      const data = await api("POST", "/customer-portal/tickets", body);
      const ticketId = data.ticket.id;

      // Upload attachments sequentially
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const upRes = await fetch(`/api/customer-portal/tickets/${ticketId}/attachments`, {
          method: "POST",
          credentials: "include",
          body: fd,
        });
        if (!upRes.ok) {
          const e = await upRes.json().catch(() => ({}));
          throw new Error(e.error || `File upload failed (${upRes.status})`);
        }
      }

      onCreated(data.ticket);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000a", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={onClose}>
      <div style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 16,
        width: "100%", maxWidth: 560, maxHeight: "92vh", overflowY: "auto",
        padding: isMobile ? "24px 20px" : "32px 32px",
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>New Ticket</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Report an issue or share feedback</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.muted, fontSize: 22, cursor: "pointer", padding: 8 }}>×</button>
        </div>

        {/* Type selector */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
          {[{ v: "issue", label: "Report an Issue", desc: "Bug or operational problem", icon: "🐛" },
            { v: "feedback", label: "Give Feedback", desc: "Ideas or suggestions", icon: "💡" }].map(t => (
            <button key={t.v} type="button" onClick={() => setType(t.v)} style={{
              padding: "14px 16px", borderRadius: 10, cursor: "pointer", textAlign: "left",
              border: `2px solid ${type === t.v ? C.accent : C.border}`,
              background: type === t.v ? C.accentDim : C.surface,
              transition: "all 0.15s",
            }}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>{t.icon}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: type === t.v ? C.accent : C.text }}>{t.label}</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{t.desc}</div>
            </button>
          ))}
        </div>

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Select
            label="Category"
            value={category}
            onChange={setCategory}
            options={cats}
          />
          <Input label="Subject" value={subject} onChange={setSubject} placeholder={type === "issue" ? "e.g. Survey not loading on Chrome" : "e.g. Add CSV export to report page"} required />
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 13, color: C.muted, fontWeight: 500 }}>Description <span style={{ color: C.danger }}>*</span></label>
            <textarea
              value={description} onChange={e => setDescription(e.target.value)}
              placeholder={type === "issue"
                ? "Describe what happened, steps to reproduce, and any error messages…"
                : "Share your thoughts in detail — what works well, what could be better, what you wish existed…"}
              rows={5}
              style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 14, padding: "10px 12px", resize: "vertical", fontFamily: "inherit", outline: "none" }}
            />
          </div>
          {type === "issue" && (
            <Select label="Priority" value={priority} onChange={setPriority} options={PRIORITIES} />
          )}
          {surveys.length > 0 && (
            <Select
              label="Related Survey (optional)"
              value={surveyId}
              onChange={setSurveyId}
              options={[{ value: "", label: "— None —" }, ...surveys]}
            />
          )}
          <FileUploadArea files={files} onChange={setFiles} />
          {error && <div style={{ color: C.danger, fontSize: 13 }}>{error}</div>}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 4 }}>
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn type="submit" disabled={loading}>{loading ? "Submitting…" : "Submit Ticket"}</Btn>
          </div>
        </form>
      </div>
    </div>
  );
}

function TicketDetailDrawer({ ticket: initialTicket, customer, onClose }) {
  const { isMobile } = useViewport();
  const [ticket, setTicket]     = useState(initialTicket);
  const [reply, setReply]       = useState("");
  const [replyFiles, setReplyFiles] = useState([]);
  const [sending, setSending]   = useState(false);
  const [error, setError]       = useState("");
  const threadEndRef            = useRef(null);

  // Fetch full ticket (with comments + attachments) when drawer opens
  useEffect(() => {
    api("GET", `/customer-portal/tickets/${initialTicket.id}`)
      .then(d => setTicket(d.ticket))
      .catch(() => {});
  }, [initialTicket.id]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [ticket?.comments]);

  async function sendReply() {
    if (!reply.trim() && replyFiles.length === 0) return;
    setSending(true); setError("");
    try {
      let commentId = null;
      if (reply.trim()) {
        const result = await api("POST", `/customer-portal/tickets/${ticket.id}/comments`, { content: reply.trim() });
        commentId = result.comment?.id || null;
      }
      for (const file of replyFiles) {
        const fd = new FormData();
        fd.append("file", file);
        if (commentId) fd.append("commentId", commentId);
        const uploadRes = await fetch(`/api/customer-portal/tickets/${ticket.id}/attachments`, {
          method: "POST",
          credentials: "include",
          body: fd,
        });
        if (!uploadRes.ok) {
          const errData = await uploadRes.json().catch(() => ({}));
          throw new Error(errData.error || `File upload failed (${uploadRes.status})`);
        }
      }
      setReply(""); setReplyFiles([]);
      const d = await api("GET", `/customer-portal/tickets/${ticket.id}`);
      setTicket(d.ticket);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  }

  const fmtDt = (d) => new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  const catLabel = (t, c) => ([...(t === "issue" ? ISSUE_CATEGORIES : FEEDBACK_CATEGORIES)].find(x => x.value === c)?.label || c);

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000a", zIndex: 200, display: "flex", justifyContent: "flex-end" }} onClick={onClose}>
      <div style={{
        background: C.surface, borderLeft: `1px solid ${C.border}`,
        width: isMobile ? "100%" : 520, maxWidth: "100vw",
        display: "flex", flexDirection: "column", overflowY: "hidden",
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
                <TypeChip type={ticket.type} />
                <TicketStatusBadge status={ticket.status} />
                {ticket.priority !== "normal" && (
                  <span style={{ fontSize: 10, fontWeight: 700, color: ticket.priority === "urgent" ? C.danger : C.warning, textTransform: "uppercase" }}>
                    {ticket.priority}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, wordBreak: "break-word" }}>{ticket.subject}</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                {catLabel(ticket.type, ticket.category)} · {fmtDt(ticket.createdAt)}
                {ticket.survey && ` · ${ticket.survey.title}`}
              </div>
            </div>
            <button onClick={onClose} style={{ background: "none", border: "none", color: C.muted, fontSize: 22, cursor: "pointer", padding: 8, flexShrink: 0 }}>×</button>
          </div>
        </div>

        {/* Thread */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Original description */}
          <div style={{ background: C.card, borderRadius: 10, padding: "14px 16px", border: `1px solid ${C.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.accent }}>
                {ticket.portalUser ? `${ticket.portalUser.firstName} ${ticket.portalUser.lastName}`.trim() : "You"}
              </span>
              <span style={{ fontSize: 11, color: C.muted }}>{fmtDt(ticket.createdAt)}</span>
            </div>
            <div style={{ fontSize: 13, color: C.textDim, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{ticket.description}</div>
            {(ticket.attachments || []).filter(a => !a.commentId).length > 0 && (
              <div style={{ marginTop: 10 }}>
                <AttachmentList attachments={(ticket.attachments || []).filter(a => !a.commentId)} ticketId={ticket.id} />
              </div>
            )}
          </div>

          {/* Comments */}
          {(ticket.comments || []).map(c => {
            const isAdmin = c.authorType === "admin";
            return (
              <div key={c.id} style={{
                background: isAdmin ? "rgba(79,110,247,0.08)" : C.card,
                borderRadius: 10, padding: "14px 16px",
                border: `1px solid ${isAdmin ? C.accent + "44" : C.border}`,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: isAdmin ? C.accent : C.text }}>
                    {isAdmin ? "Rik.ai Support" : c.authorName}
                  </span>
                  <span style={{ fontSize: 11, color: C.muted }}>{fmtDt(c.createdAt)}</span>
                </div>
                <div style={{ fontSize: 13, color: C.textDim, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{c.content}</div>
                {(ticket.attachments || []).filter(a => a.commentId === c.id).length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <AttachmentList attachments={(ticket.attachments || []).filter(a => a.commentId === c.id)} ticketId={ticket.id} />
                  </div>
                )}
              </div>
            );
          })}

          {/* Resolution note */}
          {ticket.resolutionNote && (
            <div style={{ background: "rgba(16,185,129,0.08)", borderRadius: 10, padding: "14px 16px", border: `1px solid ${C.success}44` }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.success, marginBottom: 6 }}>Resolution Note</div>
              <div style={{ fontSize: 13, color: C.textDim, lineHeight: 1.7 }}>{ticket.resolutionNote}</div>
            </div>
          )}

          <div ref={threadEndRef} />
        </div>

        {/* Reply box — hidden when closed */}
        {ticket.status !== "closed" && (
          <div style={{ padding: "16px 24px", borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
            <textarea
              value={reply} onChange={e => setReply(e.target.value)}
              placeholder="Add a reply or more details…"
              rows={3}
              style={{ width: "100%", background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, padding: "10px 12px", resize: "none", fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
            />
            <div style={{ marginTop: 8 }}>
              <FileUploadArea files={replyFiles} onChange={setReplyFiles} />
            </div>
            {error && <div style={{ color: C.danger, fontSize: 12, marginTop: 4 }}>{error}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
              <Btn disabled={(!reply.trim() && replyFiles.length === 0) || sending} onClick={sendReply}>{sending ? "Sending…" : "Send Reply"}</Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PROFILE PAGE
// ══════════════════════════════════════════════════════════════════════════════
function ProfilePage({ customer, onBack }) {
  const { isMobile } = useViewport();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword]         = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwLoading, setPwLoading]             = useState(false);
  const [pwError, setPwError]                 = useState("");
  const [pwSuccess, setPwSuccess]             = useState("");

  async function handleChangePassword(e) {
    e.preventDefault();
    setPwError(""); setPwSuccess("");
    if (newPassword.length < 8) return setPwError("New password must be at least 8 characters.");
    if (newPassword !== confirmPassword) return setPwError("Passwords do not match.");
    setPwLoading(true);
    try {
      await api("POST", "/customer-portal/change-password", { currentPassword, newPassword });
      setPwSuccess("Password changed successfully.");
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (err) {
      setPwError(err.message);
    } finally {
      setPwLoading(false);
    }
  }

  const INP_STYLE = {
    width: "100%", background: C.surface, border: `1px solid ${C.border}`,
    borderRadius: 8, color: C.text, fontSize: 14, padding: "10px 14px",
    fontFamily: "inherit", outline: "none", boxSizing: "border-box",
  };
  const ROW = { display: "flex", gap: 12, alignItems: "center", padding: "12px 0", borderBottom: `1px solid ${C.border}` };
  const LABEL = { fontSize: 12, color: C.muted, width: isMobile ? 100 : 140, flexShrink: 0 };
  const VAL = { fontSize: 14, color: C.text, fontWeight: 500 };

  return (
    <div style={{ maxWidth: 640, margin: "0 auto", padding: isMobile ? "24px 16px" : "40px 32px" }}>
      <button onClick={onBack} style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer", marginBottom: 24, padding: 0, display: "flex", alignItems: "center", gap: 6 }}>
        ← Back
      </button>

      <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700, marginBottom: 32 }}>My Profile</h1>

      {/* Account details */}
      <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, padding: "4px 24px", marginBottom: 28 }}>
        <div style={{ ...ROW, borderBottom: "none", paddingBottom: 16 }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: `linear-gradient(135deg, ${C.purple}, ${C.blue})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
            {(customer.firstName?.[0] || customer.company?.companyName?.[0] || "?").toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>{customer.firstName} {customer.lastName}</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{customer.company?.companyName} · {customer.role}</div>
          </div>
        </div>
        <div style={ROW}><span style={LABEL}>Email</span><span style={VAL}>{customer.email}</span></div>
        <div style={ROW}><span style={LABEL}>Phone</span><span style={{ ...VAL, color: customer.phone ? C.text : C.muted }}>{customer.phone || "—"}</span></div>
        <div style={ROW}><span style={LABEL}>Role</span><span style={{ ...VAL, textTransform: "capitalize" }}>{customer.role}</span></div>
        <div style={{ ...ROW, borderBottom: "none" }}><span style={LABEL}>Company</span><span style={VAL}>{customer.company?.companyName}</span></div>
      </div>

      {/* Change password */}
      <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, padding: "20px 24px" }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 18 }}>Change Password</h2>
        <form onSubmit={handleChangePassword} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, color: C.muted, display: "block", marginBottom: 6 }}>CURRENT PASSWORD</label>
            <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} style={INP_STYLE} required />
          </div>
          <div>
            <label style={{ fontSize: 12, color: C.muted, display: "block", marginBottom: 6 }}>NEW PASSWORD</label>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={INP_STYLE} required placeholder="Minimum 8 characters" />
          </div>
          <div>
            <label style={{ fontSize: 12, color: C.muted, display: "block", marginBottom: 6 }}>CONFIRM NEW PASSWORD</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} style={INP_STYLE} required />
          </div>
          {pwError && <div style={{ color: C.danger, fontSize: 13 }}>{pwError}</div>}
          {pwSuccess && <div style={{ color: C.success, fontSize: 13 }}>{pwSuccess}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Btn type="submit" disabled={pwLoading}>{pwLoading ? "Saving…" : "Update Password"}</Btn>
          </div>
        </form>
      </div>
    </div>
  );
}

function SupportPage({ customer, initialTicketId, onClearInitialTicket }) {
  const { isMobile } = useViewport();
  const [tickets, setTickets]       = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage]             = useState(1);
  const [showNew, setShowNew]       = useState(false);
  const [selected, setSelected]     = useState(null);
  const [toast, setToast]           = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 20 });
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (typeFilter   !== "all") params.set("type", typeFilter);
    api("GET", `/customer-portal/tickets?${params}`)
      .then(d => { setTickets(d.tickets || []); setTotal(d.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [statusFilter, typeFilter, page]);

  useEffect(() => { load(); }, [load]);

  // Auto-open a specific ticket when navigated from a dashboard alert
  useEffect(() => {
    if (!initialTicketId) return;
    api("GET", `/customer-portal/tickets/${initialTicketId}`)
      .then(d => { setSelected(d.ticket); onClearInitialTicket && onClearInitialTicket(); })
      .catch(() => { onClearInitialTicket && onClearInitialTicket(); });
  }, [initialTicketId]); // eslint-disable-line react-hooks/exhaustive-deps

  function onCreated(ticket) {
    setShowNew(false);
    setToast("Ticket submitted. Our team will respond shortly.");
    setTimeout(() => setToast(""), 3500);
    load();
  }

  const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const catLabel = (t, c) => ([...(t === "issue" ? ISSUE_CATEGORIES : FEEDBACK_CATEGORIES)].find(x => x.value === c)?.label || c);

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: isMobile ? "24px 16px" : "32px 24px" }}>
      {toast && <Toast message={toast} onDone={() => setToast("")} />}

      {/* Page title */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700 }}>Support & Feedback</div>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>
            Report issues or share feedback with the Rik.ai team
          </div>
        </div>
        <Btn onClick={() => setShowNew(true)}>+ New Ticket</Btn>
      </div>

      {/* Filter row */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
        {[
          { value: "all",       label: "All" },
          { value: "open",      label: "Open" },
          { value: "in_review", label: "In Review" },
          { value: "resolved",  label: "Resolved" },
          { value: "closed",    label: "Closed" },
        ].map(f => (
          <button key={f.value} onClick={() => { setStatusFilter(f.value); setPage(1); }} style={{
            padding: "6px 14px", borderRadius: 6, fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit",
            background: statusFilter === f.value ? C.accent : C.card,
            color:      statusFilter === f.value ? "#fff"   : C.muted,
          }}>{f.label}</button>
        ))}
        <div style={{ width: 1, background: C.border, margin: "4px 2px" }} />
        {[
          { value: "all",      label: "All types" },
          { value: "issue",    label: "Issues" },
          { value: "feedback", label: "Feedback" },
        ].map(f => (
          <button key={f.value} onClick={() => { setTypeFilter(f.value); setPage(1); }} style={{
            padding: "6px 14px", borderRadius: 6, fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit",
            background: typeFilter === f.value ? C.surface : "transparent",
            color:      typeFilter === f.value ? C.text    : C.muted,
            border: `1px solid ${typeFilter === f.value ? C.border : "transparent"}`,
          }}>{f.label}</button>
        ))}
      </div>

      {/* Ticket list */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: C.muted }}>Loading…</div>
      ) : tickets.length === 0 ? (
        <Card style={{ textAlign: "center", padding: "48px 24px" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🎫</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>No tickets yet</div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
            {statusFilter !== "all" || typeFilter !== "all"
              ? "No tickets match the current filters."
              : "Report an issue or share feedback and we'll get back to you."}
          </div>
          <Btn onClick={() => setShowNew(true)}>Submit a Ticket</Btn>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {tickets.map(t => (
            <Card key={t.id} onClick={() => setSelected(t)} style={{ cursor: "pointer", padding: isMobile ? "14px 16px" : "16px 20px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
                    <TypeChip type={t.type} />
                    <TicketStatusBadge status={t.status} />
                    {t.priority !== "normal" && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: t.priority === "urgent" ? C.danger : C.warning, textTransform: "uppercase" }}>
                        {t.priority}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: isMobile ? "normal" : "nowrap" }}>
                    {t.subject}
                  </div>
                  <div style={{ display: "flex", gap: 12, fontSize: 12, color: C.muted, flexWrap: "wrap" }}>
                    <span>{catLabel(t.type, t.category)}</span>
                    {t.survey && <span>· {t.survey.title}</span>}
                    <span>· {fmtDate(t.createdAt)}</span>
                    {t._count?.comments > 0 && <span>· {t._count.comments} {t._count.comments === 1 ? "reply" : "replies"}</span>}
                  </div>
                </div>
                <div style={{ color: C.muted, fontSize: 18, flexShrink: 0 }}>›</div>
              </div>
            </Card>
          ))}

          {/* Pagination */}
          {Math.ceil(total / 20) > 1 && (
            <div style={{ display: "flex", gap: 8, justifyContent: "center", paddingTop: 8 }}>
              <Btn variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ padding: "6px 14px", fontSize: 12 }}>← Prev</Btn>
              <span style={{ padding: "8px 12px", fontSize: 12, color: C.muted }}>Page {page} of {Math.ceil(total / 20)}</span>
              <Btn variant="ghost" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)} style={{ padding: "6px 14px", fontSize: 12 }}>Next →</Btn>
            </div>
          )}
        </div>
      )}

      {showNew && <NewTicketModal customer={customer} onClose={() => setShowNew(false)} onCreated={onCreated} />}
      {selected && <TicketDetailDrawer ticket={selected} customer={customer} onClose={() => { setSelected(null); load(); }} />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// FORCE CHANGE PASSWORD — shown on first login when mustChangePassword=true
// ══════════════════════════════════════════════════════════════════════════════
function ForceChangePasswordPage({ onSuccess }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword,     setNewPassword]     = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error,  setError]  = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) { setError("New passwords do not match."); return; }
    setSaving(true);
    try {
      await api("POST", "/customer-portal/change-password", { currentPassword, newPassword });
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 420, background: C.card, borderRadius: 16, border: `1px solid ${C.border}`, padding: 36 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 8 }}>Set Your Password</h2>
          <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
            Your account was created with a temporary password. Please set a new password to continue.
          </p>
        </div>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Input label="Temporary Password" type="password" value={currentPassword} onChange={setCurrentPassword} placeholder="Enter the password you received" required />
          <Input label="New Password" type="password" value={newPassword} onChange={setNewPassword} placeholder="Min 8 chars, uppercase, number, symbol" required />
          <Input label="Confirm New Password" type="password" value={confirmPassword} onChange={setConfirmPassword} placeholder="Repeat new password" required />
          {error && <p style={{ color: C.danger, fontSize: 13, margin: 0 }}>{error}</p>}
          <Btn type="submit" disabled={saving}>{saving ? "Saving…" : "Set Password & Continue"}</Btn>
        </form>
        <p style={{ fontSize: 12, color: C.muted, marginTop: 16, textAlign: "center" }}>
          Password requirements: 8+ characters, uppercase letter, number, special character.
        </p>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ROOT APP — phase router
// ══════════════════════════════════════════════════════════════════════════════
// Named exports for component-level testing (the editor carries the survey
// approval / edit-lock lifecycle logic).
export { CreateSurveyPage };

export default function App() {
  const [phase, setPhase]           = useState("loading");
  const [customer, setCustomer]     = useState(null);
  const [consentDocuments, setConsentDocuments] = useState([]);
  const [activeSurveyId, setActiveSurveyId]   = useState(null);
  const [surveyInitialTab, setSurveyInitialTab] = useState("overview");
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [projectInitialTab, setProjectInitialTab] = useState("overview");
  const [editPrefill, setEditPrefill]         = useState(null);
  const [initialTicketId, setInitialTicketId] = useState(null);
  const [navStack, setNavStack]               = useState([]); // history of views for Back

  // ── Navigation history ──────────────────────────────────────────────────────
  // Single-phase router with a back-stack so "Back" returns to wherever the user
  // actually came from (e.g. a survey opened from a project goes back to that
  // project, not always the dashboard).
  const PHASE_LABEL = { dashboard: "Dashboard", project: "Project", detail: "Survey", report: "Report", support: "Support", profile: "Profile", create: "Editor" };
  const currentView = () => ({ phase, activeSurveyId, surveyInitialTab, activeProjectId, projectInitialTab, editPrefill, initialTicketId });
  const applyView = (v) => {
    setActiveSurveyId(v.activeSurveyId ?? null);
    setSurveyInitialTab(v.surveyInitialTab ?? "overview");
    setActiveProjectId(v.activeProjectId ?? null);
    setProjectInitialTab(v.projectInitialTab ?? "overview");
    setEditPrefill(v.editPrefill ?? null);
    setInitialTicketId(v.initialTicketId ?? null);
    setPhase(v.phase);
  };
  // Push the current view onto the stack, then navigate forward applying `patch`.
  const pushTo = (nextPhase, patch = {}) => {
    setNavStack(s => [...s, currentView()]);
    if ("activeSurveyId"    in patch) setActiveSurveyId(patch.activeSurveyId);
    if ("surveyInitialTab"  in patch) setSurveyInitialTab(patch.surveyInitialTab);
    if ("activeProjectId"   in patch) setActiveProjectId(patch.activeProjectId);
    if ("projectInitialTab" in patch) setProjectInitialTab(patch.projectInitialTab);
    if ("editPrefill"       in patch) setEditPrefill(patch.editPrefill);
    if ("initialTicketId"   in patch) setInitialTicketId(patch.initialTicketId);
    setPhase(nextPhase);
  };
  // Top-level nav (header) — clears history.
  const resetTo = (nextPhase) => { setNavStack([]); applyView({ phase: nextPhase }); };
  const goBack = () => {
    if (navStack.length === 0) { applyView({ phase: "dashboard" }); return; }
    const prev = navStack[navStack.length - 1];
    setNavStack(navStack.slice(0, -1));
    applyView(prev);
  };
  const backLabel = navStack.length > 0 ? (PHASE_LABEL[navStack[navStack.length - 1].phase] || "Back") : "Back";


  // Restore session via httpOnly cookie (no localStorage check needed)
  useEffect(() => {
    api("GET", "/customer-portal/me")
      .then((data) => {
        setCustomer(data.user);
        if (data.consentRequired && data.consentDocuments?.length > 0) {
          setConsentDocuments(data.consentDocuments);
          setPhase("consent");
          return;
        }
        setPhase(data.user?.mustChangePassword ? "force-change-pw" : "dashboard");
      })
      .catch(() => setPhase("login"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Global logout hook — used by api() on 401
  useEffect(() => {
    window.__cpLogout = () => {
      setCustomer(null);
      setPhase("login");
    };
    return () => { delete window.__cpLogout; };
  }, []);

  const handleLogin = (user, data) => {
    setCustomer(user);
    if (data?.consentRequired && data.consentDocuments?.length > 0) {
      setConsentDocuments(data.consentDocuments);
      setPhase("consent");
      return;
    }
    setPhase(user?.mustChangePassword ? "force-change-pw" : "dashboard");
  };

  const handleSignOut = () => {
    fetch("/api/auth/customer-portal/logout", { method: "POST", credentials: "include" }).catch(() => {});
    setCustomer(null);
    setPhase("login");
  };

  const goEditSurvey = (survey) => {
    pushTo("create", { editPrefill: {
      editId: survey.id,
      status: survey.status,
      title: survey.title, category: survey.category, agent: survey.agent,
      multilingualEnabled: survey.multilingualEnabled, modality: survey.modality, surveyType: survey.surveyType,
      estimatedMins: survey.estimatedMins, description: survey.description,
      purpose: survey.purpose, instructions: survey.instructions,
      targetSampleSize: survey.targetSampleSize, incentiveType: survey.incentiveType,
      incentiveValue: survey.incentiveValue, closeDate: survey.closeDate,
      targetAgeBands: survey.targetAgeBands, targetGenders: survey.targetGenders,
      targetCityTiers: survey.targetCityTiers,
      targetEducation: survey.targetEducation, targetIncomeBands: survey.targetIncomeBands,
      targetProfessions: survey.targetProfessions, targetHouseholdRoles: survey.targetHouseholdRoles,
      targetYearsWorking: survey.targetYearsWorking, targetIndustries: survey.targetIndustries,
      questions: survey.questions,
    } });
  };

  const goViewSurvey = (id, tab = "overview") => pushTo("detail", { activeSurveyId: id, surveyInitialTab: tab });

  const goViewProject = (id, tab = "overview") => {
    localStorage.setItem("lastViewedProjectId", id);
    pushTo("project", { activeProjectId: id, projectInitialTab: tab });
  };

  if (phase === "loading") {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: C.muted, fontSize: 14 }}>Loading…</div>
      </div>
    );
  }

  if (phase === "consent") {
    return (
      <ConsentGateScreen
        documents={consentDocuments}
        onAccepted={() => { setConsentDocuments([]); setPhase("dashboard"); }}
      />
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "'Poppins', sans-serif", color: C.text, overflowX: "hidden" }}>
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {phase !== "login" && customer && (
        <Header customer={customer} onSignOut={handleSignOut} onDashboard={() => resetTo("dashboard")} onSupport={() => resetTo("support")} onProfile={() => resetTo("profile")} />
      )}

      {phase === "login" && <LoginPage onLogin={handleLogin} />}

      {phase === "force-change-pw" && (
        <ForceChangePasswordPage
          onSuccess={() => {
            setCustomer(prev => prev ? { ...prev, mustChangePassword: false } : prev);
            setPhase("dashboard");
          }}
        />
      )}

      {phase === "dashboard" && (
        <DashboardPage
          customer={customer}
          onViewSurvey={goViewSurvey}
          onViewProject={goViewProject}
          onViewTicket={(ticketId) => pushTo("support", { initialTicketId: ticketId })}
        />
      )}

      {phase === "create" && (
        <CreateSurveyPage
          customer={customer}
          prefill={editPrefill}
          onBack={goBack}
          onSuccess={(id) => { setActiveSurveyId(id); setPhase("detail"); }}
        />
      )}

      {phase === "detail" && activeSurveyId && (
        <SurveyDetailPage
          surveyId={activeSurveyId}
          customer={customer}
          initialTab={surveyInitialTab}
          onBack={goBack}
          backLabel={backLabel}
          onEdit={goEditSurvey}
        />
      )}

      {phase === "project" && activeProjectId && customer && (
        <ProjectWorkspacePage
          projectId={activeProjectId}
          customerId={customer.customerId}
          initialTab={projectInitialTab}
          onBack={goBack}
          backLabel={backLabel}
          onViewSurvey={goViewSurvey}
        />
      )}

      {phase === "support" && customer && (
        <SupportPage customer={customer} initialTicketId={initialTicketId} onClearInitialTicket={() => setInitialTicketId(null)} />
      )}

      {phase === "profile" && customer && (
        <ProfilePage customer={customer} onBack={goBack} />
      )}

      {phase !== "login" && phase !== "force-change-pw" && (
        <footer style={{ borderTop: `1px solid ${C.border}`, padding: "14px 32px", textAlign: "center", fontSize: 11, color: C.muted }}>
          &copy; 2026 Rik Systems LLP &nbsp;&middot;&nbsp;
          <a href="/api/legal/customer/privacy_policy/page" target="_blank" rel="noopener" style={{ color: C.muted, textDecoration: "underline" }}>Privacy Policy</a>
          &nbsp;&middot;&nbsp;
          <a href="/api/legal/customer/terms_of_service/page" target="_blank" rel="noopener" style={{ color: C.muted, textDecoration: "underline" }}>Terms of Service</a>
          &nbsp;&middot;&nbsp;
          <a href="mailto:contact@rikai.tech" style={{ color: C.muted, textDecoration: "underline" }}>contact@rikai.tech</a>
        </footer>
      )}
    </div>
  );
}
